/**
 * WCS bounds — TEXT / MTEXT / ATTDEF box-corner math (T1 arm (c) — see `./wcsBoundsMath.ts` for the
 * shared OCS/AABB primitives this module builds local (pre-OCS-transform) corner sets for).
 *
 * `entityBounds` was previously deferred specifically because the only prior bounds source
 * (`viewportMembership.ts::textBounds`/`mtextBounds`) reported the INSERTION POINT ONLY — no glyph
 * extent at all. This module closes that gap with a real, justification-aware estimate.
 *
 * # ERROR BUDGET — what this estimate promises, and what it does not
 *
 * Read this first. A finding against the MTEXT box is INSIDE the budget if it names a residual
 * below with a magnitude no larger than the one stated; it is OUTSIDE — and therefore real — if it
 * names an under-report that is not listed, or one larger than its stated bound.
 *
 * ## What the estimate IS
 *
 * A STRUCTURAL approximation, not a measurement. `packages/drawing-model` has no font:
 * `.dependency-cruiser.cjs` forbids `packages/**` → `apps/web`, the SDF atlases live in
 * `apps/web/public/sdf-atlases`, and DXF TEXT/MTEXT carry no resolved-width field. So the box is
 * built from the renderer's own layout ARITHMETIC (`mtextParser.ts` → `textRunRenderer.ts` /
 * `shxRunRenderer.ts` / `sceneTextMeshFactories.ts`, mirrored term for term) with per-glyph BOUNDS
 * — {@link MAX_GLYPH_ADVANCE}, {@link MAX_GLYPH_INK}, {@link MAX_GLYPH_DESCENT} — for the glyphs it
 * cannot see, all three re-derived from the shipped atlases at test time by
 * `apps/web/…/text/__tests__/mtextGlyphMetricBound.test.ts`.
 *
 * Groups 42/43 are NOT this: AutoCAD's own computed dimensions, returned verbatim whenever carried
 * (`> 0`), so nothing below applies to the SEMANTIC box of a conformantly-written file. The
 * rectangle the MESH paints is a SEPARATE statement and is not exempt — the renderer never reads
 * those groups, so it is walked from the contents on EVERY branch (F1).
 *
 * ## The declared OVER-report, and why that direction
 *
 * A per-glyph bound applied per glyph compounds. MEASURED on the 43-glyph pangram "The quick brown
 * fox jumps over the lazy dog" at the default height, against the atlases' own metrics:
 *
 * | axis | ordinary prose | worst bundled glyph repeated |
 * |---|---|---|
 * | width  | 3.36× (`OpenGost-TypeB`) – 3.57× (`Roboto`) | ≤ 1.01× |
 * | height | 1.46× – 1.58× | 1.18× (cap 0.80952 + descent 0.28571) |
 *
 * That is deliberate: an UNDER-reported box drops painted glyphs out of cache-direct culling,
 * selection and INSERT footprints — silently, and in the only direction a user cannot see. The
 * over-report is BOUNDED and homogeneous of degree 1 in `nominalHeight`, so it cannot become the
 * other failure mode: the two canvas-blanking regressions this module was written against (group 41
 * = 210 713 verbatim, and 1e13 from two uncorrelated global maxima) are now structurally
 * impossible.
 *
 * ## The residual UNDER-reports — magnitude, and the issue that closes each
 *
 * One entry per gap, each with the work that deletes it, so the two cannot drift apart. An UNLISTED
 * gap is a real finding, not a budgeted one: a term that cannot be bounded gets a line here with
 * its magnitude and its blocker, or it gets implemented.
 *
 * U1. **A REGISTERED SHX font** — unbounded in principle. `fontRegistry.ts` resolves SHX names
 *     against user-supplied bytes, so the metrics are whatever that file carries. The repo's own
 *     `cs_gost2304.shx` fixture reaches `advanceX / above` = 1.68 against {@link
 *     MAX_GLYPH_ADVANCE}'s 1.22 (1.38× short) and `below / above` = 12/40 = 0.30 against {@link
 *     MAX_GLYPH_DESCENT}'s 0.29 (1.03× short). UNREGISTERED names fall back to the bundled OpenGost
 *     atlases, which ARE bounded. CLOSED BY #795 — the fidelity-complete answer: real per-glyph
 *     advance, ink, bearing, cap and descent for the resolved style, a registered SHX font's header
 *     included. Not an import — `.dependency-cruiser.cjs` forbids `packages/**` → `apps/web` and
 *     that direction is correct — but a metrics PORT the app registers into `drawing-model`. It
 *     collapses the whole over-report to the measurement.
 * U2. **Decoration quads** (`\\L` / `\\O` / `\\K`) — unbounded as the run height falls. Both
 *     renderers use `thickness = max(heightWorld × 0.05, 1)`, a floor in WORLD units, and put the
 *     underline at `-1.5 × thickness`, the overline at `+0.8 × heightWorld + thickness`. Above
 *     `heightWorld = 20` that is 0.075 / 0.85 of a run height and INSIDE the box; below it the
 *     floor dominates — at the 2.5 default, 0.6 run heights below against the interval's 0.29 and
 *     1.2 above against 1.0; at `heightWorld = 0.01`, 150 run heights. NOT folded in: the term is
 *     not homogeneous in `nominalHeight`, so it would break the annotative law AND report an
 *     enormous box for small text. The defect is the renderer's world-unit floor, not this bound.
 *     CLOSED BY #796 — make that thickness zoom-relative (`textRunRenderer.ts` /
 *     `shxRunRenderer.ts`), then fold its reach into the interval.
 * U3. **TEXT / ATTDEF width** (not MTEXT) uses {@link AVERAGE_GLYPH_ADVANCE} = 0.6, an AVERAGE: a
 *     TEXT of `WWWW` is under-reported by about 2× (`Roboto`'s `W` advances 0.673). Shared verbatim
 *     with three other call sites, so widening it HERE alone would put this module out of step with
 *     all three. CLOSED BY #797 — converge the four call sites onto a bound. The HEIGHT axis of the
 *     same box is NOT a residual: the descender band below the baseline is inside it as of round
 *     10's finding A ({@link textLocalBoxCorners}).
 * U4. **Column wrapping** — the height counts PARAGRAPHS (`layout.lineCount`), not wrapped lines.
 *     Costs nothing at this commit because nothing in the repo wraps (verified end to end in {@link
 *     mtextEstimatedWidth}'s term 2); it under-counts lines the day wrapping lands. CLOSED BY #798
 *     — real wrapping, in the renderer AND here (which also settles the paragraph-vs-wrapped-line
 *     count).
 * U5. **Two LATENT sub-run offsets**, neither with a production call site at this commit, both
 *     joining the interval arithmetic the day they get one: `mtextStack.ts::composeStackRun`'s
 *     ±0.35 × `heightWorld` for `\\S`, and `\\f`'s Q29.A embedded block (unbounded in principle).
 *     Live, the paths render the parser's single full-height `num/den` run, whose
 *     `baselineOffsetWorld` comes from `\\A` alone and IS covered.
 *
 * ## The FRAME, which is not this estimate's error but is measured against it
 *
 * F1. The live MTEXT mesh IGNORES group 71 — CONTAINED since round 10, at a declared cost.
 *     `sceneTextMeshFactories.ts` puts line 0's baseline at the mesh origin and `mtextGeometry.ts`
 *     sets that origin to the insertion point, with no attachment offset anywhere, so the renderer
 *     draws every MTEXT as if it were bottom-left-anchored on line 0's baseline while this module
 *     anchors the DXF-semantic extents box by the 1-9 grid. The escape is `0.81 × runHeight_r`, so
 *     an inline `\\H` makes it UNBOUNDED: a default top-left `\\H100x;A` at the 2.5 nominal had
 *     model bounds `[-322.5, 0]` in Y against a rendered cap at ≈ +202. The render-side AABB does
 *     not absorb it — that one is `± mtextBlockSpan(nominalHeight, …)`, i.e. `±2.5` for the same
 *     entity, the separate defect #802 (@codex PR #794 round 10 P2, finding C).
 *
 *     {@link mtextLocalBoxRects} builds BOTH rectangles in one local frame, and flips the mesh
 *     one's y-interval end for end when the group-210 normal points down, because the renderer
 *     draws such a run with `mesh.scale.set(1, -1, 1)` in that same frame (round 11 P2, finding 3;
 *     `wcsBoundsMath.ts::isReflectedOcs` is the one-truth bit, read from the MODEL). BOUNDS take
 *     the two rectangles' HULL ({@link mtextAnchoredBoxCorners}) — a bounding box IS a hull — at an
 *     over-report bounded by the group-71 offset itself: at most the box's own extent per axis,
 *     exactly ZERO on X for the left column 1/4/7, exactly `paintedAbove` on Y at AutoCAD's default
 *     top-left. MEASURED: a 2-paragraph block at the 2.5 nominal goes from 7.225 to 9.725 tall and
 *     does not move on X; a right-attached single line doubles on X. SELECT takes those same two
 *     rectangles — bar the one edge the next paragraph narrows — as TWO SEPARATE RINGS
 *     ({@link mtextAnchoredBoxRings}) and never their hull, since two rectangles need not COVER
 *     the rectangle that spans them (round 11 P2, finding 2 — that builder's doc has the
 *     geometry).
 *
 *     The mesh rectangle is the CONTENTS WALK on EVERY branch, groups 42/43 carried or not, so it
 *     carries NO residual of its own — it is the same four reaches as an estimated MTEXT's, under
 *     exactly U1-U5 and nothing else. Round 11 gave the authoritative branch conservative one-line
 *     bands at the nominal height instead; round 12 P2 found them far too shallow, since paragraph
 *     `k` is based at `-k ×` {@link mtextLineAdvance} and a bottom-attached `A\\PB` at the 2.5
 *     nominal paints to ≈ -4.725 where the band reached -0.725, which the bottom row's upward
 *     semantic rectangle cannot cover. DECLARED COST: the authoritative branch no longer skips the
 *     contents.
 *
 *     THE TWO FACETS DIVERGE A SECOND TIME, ON THE MESH RECTANGLE'S RIGHT EDGE (round 13 P2).
 *     BOUNDS keep the walked `paintedRight` even where group 42 is carried — group 42 is AutoCAD's
 *     column for ITS font, the walk is this renderer's reach with the BUNDLED atlases, and a
 *     prefilter is the one place that must cover a bundled font WIDER than AutoCAD's; where the
 *     walk is wider the hull widens with it, carrying the prose over-report (3.4-3.6×) onto a file
 *     that used to be capped by group 42. SELECT may not do that: it is the EXACT crossing test,
 *     and on a 36-glyph authoritative-42 witness at the 2.5 nominal the walk reaches 112.785 where
 *     the three bundled atlases paint 26-31 and group 42 ends at 50 — a crossing window anywhere in
 *     the 62-unit empty strip selected the MTEXT, the same false-accept class as round 11's hull.
 *     So the select ring's mesh right edge is `selectPaintedRight`, the same walk clamped by group
 *     42, which is AutoCAD's measurement of the SAME string and still covers the ink. BOUNDS ⊇
 *     SELECT, always. SELECT's own DECLARED COST, the mirror of the prefilter's: where the bundled
 *     atlases really are wider than AutoCAD's font, the exact ring stops short of painted ink on X
 *     by at most `paintedRight − group 42` — the prefilter still covers it, a crossing test over
 *     the visible glyphs still hits, and U1/#795 collapses the two numbers onto the measurement. X
 *     ONLY: the Y walk is `mtextLineAdvance` (computed, not bounded) plus {@link MAX_GLYPH_DESCENT}
 *     = 0.29 against a measured 0.2857, ≈1.2× and not 3.4×, and group 43 would UNDER-cover it —
 *     `[0, h43]` excludes the descent below line 0's baseline (round 11, finding 1) and the whole
 *     paragraph stack below it (round 12). #801 still collapses the pair into one.
 *
 *     THAT CLAMP HAS A PRECONDITION, HELD UP FROM BOTH ENDS (rounds 14 + 15 P2). `min(walk, group
 *     42)` is only safe while group 42 describes the CURRENT layout. It stopped being true after
 *     an edit: the live Properties path patched `contents` / `nominalHeight` / `referenceWidth`
 *     and left the IMPORTED measurement alone, so the clamp truncated the exact ring to the OLD
 *     width while the renderer painted the NEW string and a crossing window over the visible
 *     suffix MISSED — this round's own false-accept, mirrored. WRITE SIDE, the fix that repairs
 *     BOTH facets: an edit that changes the laid-out block CLEARS groups 42/43 in the
 *     authoritative write contour — `command-engine::mtextLayoutMeasurementInvalidation` for an
 *     ENTITY edit (a prev→next FIELD diff, applied by the reducer's post-steps MTEXT
 *     normalization so ONE `EntityMutation` carries the old pair for undo, because an apply
 *     reaches an MTEXT through the typed writer AND the legacy translator), and
 *     `::mtextStyleMeasurementInvalidation` for a TEXT STYLE edit, which changes the resolved
 *     FONT while changing no entity field at all (round 15 P2) — which is also what AutoCAD does,
 *     it RECOMPUTES them on edit. SCALE was the THIRD writer and round 16 P2 (#804) closes it:
 *     `command-engine::scaleEntityRadii` SCALES the pair where the scaled block stays SIMILAR,
 *     CLEARS it where an absolute inline `\\H` breaks that. READ SIDE, `mtextMeasurement-
 *     Freshness.ts::mtextCachedWidthCouldDescribeContents` is a COARSE PLAUSIBILITY FLOOR and
 *     NOT a freshness test — it catches only a group 42 implausibly SMALL for this walk, a
 *     MODERATE SCALE passes it (its own header has the worked ×2 numbers), and it can never
 *     repair the SEMANTIC rectangle below, which is group 42 VERBATIM.
 * F2. The live TEXT / ATTDEF mesh IGNORES justification — the same shape one entity type over,
 *     CONTAINED the same way. `typed/textFromEntity.ts` places the mesh at `insertionPoint` and
 *     never reads `alignmentPoint`; `buildTextEntityMesh` emits one run from `x = 0` with baseline
 *     `y = 0`; the `alignment` `textGeometry.ts` derives from groups 72/73 is written onto the
 *     SceneGeometry and never read back. So the glyphs paint baseline-left at the insertion point
 *     at the group-50 angle, whatever groups 72/73/11 say, while {@link textJustifiedLocalBox}
 *     anchors the DXF-semantic rectangle at the ALIGNMENT point (and, for Aligned/Fit, at the
 *     vector's own angle and length). {@link textMeshLocalCorners} unions the painted rectangle in,
 *     in that same local frame, with one rotate at the end — as a POINT SET to bound, never as one
 *     ring (the distinction F1 draws; no consumer builds select segments from it). The cost is
 *     bounded by the anchor offset plus the grid cell, and is ZERO for the default Left+Baseline
 *     TEXT. CLOSED BY #803 — read the `alignment` the adapter already computes.
 *
 * ## Adjacent, and not a budget entry
 *
 *   * #799 — the reason several residuals above surfaced ONE AT A TIME across nine rounds: this
 *     module MIRRORS `mtextParser.ts`'s grammar walk rather than calling it (the dependency rule,
 *     correctly, forbids the import).
 *   * #800 — split this file (the TEXT arm, the MTEXT painted-run walk and the box/corner builders
 *     are three modules), barrel-re-exported so no import path moves. It is AT the 2 000-line
 *     ceiling.
 *
 * WIDTH ESTIMATION. TEXT / ATTDEF use the `glyphs × height × widthFactor × 0.6` average-glyph
 * heuristic — {@link AVERAGE_GLYPH_ADVANCE}, whose doc names the three repo call sites it matches
 * rather than diverges from (U3, #797, is the convergence). MTEXT applies a per-glyph BOUND once
 * per painted RUN and sums along the line, because that is how the renderer advances its cursor
 * ({@link mtextPaintedLayout}): {@link MAX_GLYPH_ADVANCE} plus {@link MAX_GLYPH_INK}, the latter
 * split into {@link MAX_GLYPH_RIGHT_REACH} + {@link MAX_GLYPH_LEFT_BEARING} so the half painted
 * BEFORE the cursor lands on the left of the box (@codex PR #794 round 8 P2 finding A, round 10 P2
 * finding B).
 *
 * MTEXT gets BETTER-than-heuristic fidelity for free: `horizontalWidth`/`verticalHeight` (DXF
 * groups 42/43) are AutoCAD's OWN computed box dimensions, present whenever the file was saved by
 * AutoCAD/a conformant writer — an EXACT value, not an estimate, read verbatim when present (`> 0`
 * — a `0` means "not carried", per the schema default).
 *
 * JUSTIFICATION (Autodesk DXF reference, TEXT group 72/73 + MTEXT group 71): TEXT's anchor is the
 * ALIGNMENT point (group 11/21/31) whenever the justification is non-default AND the source carried
 * one — the INSERTION point (group 10/20/30) is the pure "first point" only for default
 * (Left+Baseline) justification. `hJustification` 3 (Aligned) / 5 (Fit) additionally pin the EXACT
 * width + angle from the insertion→alignment vector (Autodesk DXF reference: "the text angle is
 * determined by the vector from the first alignment point to the second"). MTEXT anchors at
 * `insertionPoint` per its own group-71 attachment (a 1-9 grid), independent of TEXT's 0-5/0-3
 * codes.
 */

import { mtextCachedWidthCouldDescribeContents } from "./mtextMeasurementFreshness";
import type { Local2 } from "./wcsBoundsMath";
// The LOCAL-rect box algebra these corner builders compose lives with the other kind-agnostic
// primitives in `./wcsBoundsMath.ts` — see its "LOCAL rectangle primitives" section.
import {
  anchorAndRotate,
  localBoxRect,
  positiveOr,
  rectCorners,
  rotateDeg2D,
  unionRect,
  type LocalRect,
} from "./wcsBoundsMath";

/**
 * The AVERAGE glyph advance, as a fraction of the run height — the TEXT /
 * ATTDEF font metric (see the module header for the three repo call sites
 * that already use this exact convention).
 *
 * It is an AVERAGE, not a bound: the bundled atlases paint glyphs up to
 * {@link MAX_GLYPH_ADVANCE} heights wide, so a TEXT of `WWWW` is
 * under-reported by about 2×. That is pre-existing, it is shared verbatim
 * with `cad-core/leafBounds.ts`, `scene3d/adapters/geom/mtextGeometry.ts`
 * and `cad-core/multileaderContextBounds.ts`, and widening it here alone
 * would put this module's TEXT box out of step with all three. The MTEXT
 * painted-run sum does NOT use it — see {@link MAX_GLYPH_ADVANCE} for why
 * the two deliberately differ now.
 */
const AVERAGE_GLYPH_ADVANCE = 0.6;

/**
 * The per-glyph PEN ADVANCE bound, as a fraction of the run height: the
 * widest step any glyph of any BUNDLED font moves the cursor by. Multiplies
 * the painted-glyph count, the run's `\\W` width factor and its `\\T`
 * tracking, exactly as `textRunRenderer.ts` does (`cursorX += glyph.advance ×
 * heightScale × widthFactor × trackingFactor`, `heightScale =
 * run.heightWorld / atlas.lineHeight`).
 *
 * # Why the MTEXT sum may not use {@link AVERAGE_GLYPH_ADVANCE}
 *
 * @codex PR #794 round 8 P2 (finding A). Until round 7 the estimate counted
 * RAW SOURCE CHARACTERS, so every format code padded the count and the 0.6
 * average rode on the padding rather than on the metric. Round 7's
 * painted-run sum removed the padding, so the metric has to carry the bound
 * ALONE and an average cannot: a `W` advances 37/55 = 0.673 heights in the
 * bundled Roboto atlas, so 0.6 reports a line of `W`s at 89% of the pen.
 *
 * # The measurement
 *
 * MEASURED over every glyph of the three atlases the app ships
 * (`apps/web/public/sdf-atlases/*.json`, 230 glyphs each), as
 * `advance / lineHeight` — the renderer's own ratio:
 *
 * | atlas | lineHeight | worst glyph | advance / lineHeight |
 * |---|---|---|---|
 * | `Roboto-Regular` | 55 | `№` (`∞` ties) | 43/55 = 0.7818 |
 * | `OpenGost-TypeA` | 42 | `‰` | 46/42 = 1.0952 |
 * | `OpenGost-TypeB` | 42 | `‰` | 51/42 = 1.2143 |
 *
 * `1.2143` rounded UP to the hundredth. `Roboto-Regular`'s `W`, the glyph
 * the finding names, is 37/55 = 0.6727 — inside it, and already past the 0.6
 * average; the two missing-glyph fallbacks are inside it too (`lineHeight ×
 * 0.5` on the instanced path, `(above + below) / 2` on the SHX one).
 * `apps/web/…/text/__tests__/mtextGlyphMetricBound.test.ts` re-derives every
 * constant here from those JSON files at test time — `apps/web` is the one
 * side that may read them — so rebuilding an atlas with a wider glyph fails
 * the build instead of silently un-bounding the box.
 *
 * It does NOT bound a REGISTERED SHX font: `fontRegistry.ts` resolves SHX
 * names against user-supplied bytes and the `cs_gost2304.shx` fixture
 * reaches 1.68 heights on `¼` — module header U1 (#795). Unregistered names
 * fall back to the bundled OpenGost atlases, which ARE bounded.
 */
export const MAX_GLYPH_ADVANCE = 1.22;

/**
 * The RIGHT REACH half of {@link MAX_GLYPH_INK}: how far one glyph's painted
 * quad may reach PAST the cursor it starts at, `(bearingX + w) / lineHeight`
 * — `‰` in `OpenGost-TypeB`, 50/42 = 1.1905, the worst of the three atlases
 * in that constant's table. Stated as `1.29 − ` {@link MAX_GLYPH_LEFT_BEARING}
 * rather than as its own rounded literal, so the two halves sum EXACTLY to
 * {@link MAX_GLYPH_INK} and no width in the repo moves.
 */
export const MAX_GLYPH_RIGHT_REACH = 1.194;

/**
 * The LEFT SIDE BEARING half of {@link MAX_GLYPH_INK}: how far one glyph's
 * quad may reach to the LEFT of the cursor it starts at, `-bearingX /
 * lineHeight` — `j` in both OpenGost atlases, 4/42 = 0.09524, rounded UP to
 * the thousandth.
 *
 * It is the half that lies on the OPPOSITE side of the layout column's
 * origin from everything else the width sums, which is why it has to be
 * reported as a SIDE rather than merely be inside the width:
 * `mtextEstimatedWidth` puts it into {@link MtextSideExtents}'s
 * `overhangLeft` and `localBoxRect` shifts the box left by it WITHOUT
 * widening it (@codex PR #794 round 10 P2, finding B).
 */
export const MAX_GLYPH_LEFT_BEARING = 0.096;

/**
 * The INK OVERHANG bound, as a fraction of the run height: how far one
 * glyph's painted quad may reach past the pen positions the advance sum
 * accounts for — to the right of the cursor it starts at, plus the left
 * side bearing of the glyph that starts the line.
 *
 * # Why it is a separate term, applied ONCE per line
 *
 * The box must contain painted PIXELS, not pen positions, and a quad does
 * exceed its advance: `textRunRenderer.ts` paints `[cursor + bearingX ×
 * heightScale, cursor + (bearingX + w) × heightScale]` while the cursor
 * moves by `advance × heightScale × widthFactor × trackingFactor`. The
 * excess does NOT accumulate — the cursor of glyph `i` is the sum of the
 * advances BEFORE it — so a line's painted right edge is at most
 * `Σ advances + max glyph ink` and its left edge at most one side bearing
 * before the origin. One term per line, at the widest painted run, is
 * therefore both sufficient and tighter than inflating every glyph
 * (@codex PR #794 round 8 P2, finding A).
 *
 * # The measurement
 *
 * Same three bundled atlases, as `(bearingX + w) / lineHeight` for the
 * right reach and `-bearingX / lineHeight` for the left:
 *
 * | atlas | widest ink | `(bearingX+w)/lineHeight` | left bearing | `-bearingX/lineHeight` |
 * |---|---|---|---|---|
 * | `Roboto-Regular` | `∞` | 43/55 = 0.7818 | `∫` | 4/55 = 0.0727 |
 * | `OpenGost-TypeA` | `‰` | 46/42 = 1.0952 | `j` | 4/42 = 0.0952 |
 * | `OpenGost-TypeB` | `‰` | 50/42 = 1.1905 | `j` | 4/42 = 0.0952 |
 *
 * `1.1905 + 0.0952 = 1.2857`, rounded UP to the hundredth. The two maxima
 * are ADDED rather than taken per glyph because the glyph that overhangs on
 * the right and the one that overhangs on the left may be different glyphs
 * on the same line — which is also why they are SEPARATELY exported
 * ({@link MAX_GLYPH_RIGHT_REACH}, {@link MAX_GLYPH_LEFT_BEARING}) and this
 * constant DEFINED as their sum: the scalar width needs the total, the
 * placement needs to know which half is on the other side of the anchor,
 * and a third literal would drift. The atlas test re-derives both halves.
 *
 * # Why it is scaled by the run's `max(1, widthFactor)`
 *
 * The two live paths disagree on whether `\\W` stretches the INK: the
 * instanced SDF path does NOT (`textRunRenderer.ts` builds `quadWidth =
 * glyph.w × heightScale` with no `widthFactor`, so `\\W2;W` advances 1.345
 * heights but inks 0.691), the SHX stroke path DOES (`shxRunRenderer.ts`
 * scales glyph-local x first). The bound covers both, floored at 1 because a
 * `widthFactor < 1` narrows the SHX ink but leaves the SDF quad at full
 * width. Taken per RUN with that run's own height
 * ({@link MtextPaintedLayout}'s `inkFactor`), never as a product of
 * entity-wide maxima: `{\\H1000000x;A}{\\W1000000;B}` would otherwise
 * re-create the 1e13 box round 7 killed.
 */
export const MAX_GLYPH_INK = MAX_GLYPH_RIGHT_REACH + MAX_GLYPH_LEFT_BEARING;

/**
 * The DESCENT bound, as a fraction of the run height: how far BELOW its own
 * baseline a glyph's painted quad may reach.
 *
 * # Why the shear needs it (@codex PR #794 round 9 P2)
 *
 * Round 8 gave each painted run the y-interval `[-drop_r, +runHeight_r]`,
 * `drop_r` being its own baseline's depth — on LINE 0 that is `[0,
 * runHeight_r]`, which says a run never paints below the line it sits on. It
 * does: `textRunRenderer.ts` puts a glyph quad's lower edge at `cursorY +
 * (bearingY − h) × heightScale`, and `bearingY < h` for every descender.
 *
 * That negative `y` is SHEARED by the same expression as the caps, on both
 * live paths: the instanced SDF path shears the whole quad
 * (`textShaders.glsl.ts`: `textLocal = instance_offset + instance_scale ×
 * unitCorner; textLocal.x += textLocal.y × shear`, and `instance_offset.y`
 * IS that lower edge), and the SHX stroke path shears glyph-local `y` before
 * adding the cursor (`shxRunRenderer.ts`: `wx = (sx + seg.y × tanOblique) ×
 * heightScale + cursorX`, and a stroke font's `seg.y` goes below the
 * baseline too). So a POSITIVE oblique — caps leaning RIGHT — throws its
 * descenders LEFT. With the lower end pinned at 0 on line 0, `shearLeft` for
 * `\Q89;g` came out EXACTLY zero while the renderer painted the `g` about
 * 12.5 heights to the left, and culling and selection both missed it.
 *
 * # The measurement
 *
 * Same three bundled atlases, as `(h − bearingY) / lineHeight` — the depth
 * of the quad's lower edge below the baseline, in the renderer's own ratio:
 *
 * | atlas | lineHeight | deepest glyph | `(h − bearingY)/lineHeight` |
 * |---|---|---|---|
 * | `Roboto-Regular` | 55 | `@` (`§`, `φ` tie) | 14/55 = 0.25455 |
 * | `OpenGost-TypeA` | 42 | `@` (`g`, `p`, `q`, `y`, `µ` tie) | 12/42 = 0.28571 |
 * | `OpenGost-TypeB` | 42 | `@` (same ties) | 12/42 = 0.28571 |
 *
 * `0.28571` rounded UP to the hundredth. The glyph the finding names,
 * Roboto's `g`, is 12/55 = 0.21818 — well inside it. The atlas test
 * re-derives the constant from those JSON files, so an atlas rebuilt with a
 * deeper descender fails there instead of silently un-bounding the box. The
 * TOP end needs no matching constant: it is the run's whole `runHeight_r`,
 * and the measured cap reach tops out at `²` 34/42 = 0.80952 in
 * `OpenGost-TypeB`, so one run height is a BOUND already with 19-33% to
 * spare — which the same test pins.
 *
 * # What it does NOT bound
 *
 * The same things {@link MAX_GLYPH_ADVANCE} records (module header U1/U2),
 * one of them with a SHARPER number: a REGISTERED SHX font's stroke reaches
 * `below / above` run heights down, and the repo's own `cs_gost2304.shx`
 * fixture declares 12/40 = 0.30 — past this constant by a hundredth
 * (`cs_gost2304a.shx` is 12/56 = 0.214, inside it). No constant bounds an
 * arbitrary uploaded font; unregistered names fall back to the bundled
 * OpenGost atlases, which ARE bounded. The decoration quads (`\\L` / `\\O` /
 * `\\K`) are not a fraction of the run height at all — U2, #796.
 */
export const MAX_GLYPH_DESCENT = 0.29;

/**
 * The `\A` (alignment-within-line) baseline offsets, as a factor of the
 * RUN's own height — `mtextParser.ts::applyAlignmentToRun` verbatim:
 * `0` baseline-bottom ⇒ 0, `1` middle ⇒ `-0.5`, `2` top ⇒ `-1`, `3`
 * baseline-shift ⇒ `+0.5`. The parser accepts any finite `0 ≤ v ≤ 3` and
 * stores `Math.round(v)`, which is mirrored at the call site.
 *
 * It moves a run's WHOLE painted y-interval (both renderers add it to their
 * cursor Y), so it belongs in the same interval arithmetic as the descent
 * (@codex PR #794 round 9 P2): `\A2;` sinks a run by a whole run height and
 * `\A3;` LIFTS it by half, the one input that can push a cap above
 * `runHeight_r`.
 */
const ALIGNMENT_BASELINE_FACTOR = [0, -0.5, -1, 0.5] as const;

/** Estimate a single line's rendered width — the established `height × widthFactor × 0.6` heuristic (see module doc). */
export function estimateTextWidth(text: string, height: number, widthFactor: number): number {
  return estimateGlyphRunWidth(text.length, height, widthFactor);
}

/**
 * {@link estimateTextWidth} over a GLYPH COUNT rather than a string — the one
 * definition of the TEXT / ATTDEF heuristic, for a caller that already knows
 * how many characters it has and must not build a string to say so.
 *
 * The `Math.max(count, 1)` floor is the string form's `Math.max(text.length,
 * 1)`, and `Math.max(height, …)` is a PER-LINE minimum. MTEXT does NOT come
 * through here any more — its painted-run sum uses {@link mtextPaintedBound}
 * with the two BOUND metrics instead of the average (@codex PR #794 round 8
 * P2, finding A).
 */
function estimateGlyphRunWidth(glyphCount: number, height: number, widthFactor: number): number {
  const len = Math.max(glyphCount, 1);
  return Math.max(height, height * len * Math.max(widthFactor, 1e-6) * AVERAGE_GLYPH_ADVANCE);
}

/**
 * The UPRIGHT painted width of one MTEXT line, from the two factors
 * {@link mtextPaintedLayout} measures — the ONE place the two glyph metrics
 * meet, so the estimate branch and its fallback rung cannot drift apart.
 *
 *     nominalHeight × (advanceFactor × MAX_GLYPH_ADVANCE
 *                      + inkFactor   × MAX_GLYPH_INK)
 *
 * `advanceFactor` is the line's pen travel in units of `nominalHeight`
 * (`Σ over its runs of glyphs × runHeight × widthFactor × tracking`) and
 * `inkFactor` is the widest painted run's `runHeight × max(1, widthFactor)`.
 * See both constants for the derivation and the measured worst glyphs.
 *
 * The sum has no `Math.max(nominalHeight, …)` floor because it cannot need
 * one: `inkFactor ≥ 1` for any MTEXT, painted or empty, so the second term
 * alone is already `≥ 1.29 × nominalHeight` (the same reason
 * {@link mtextBlockSpan}'s additive form dropped its floor). The old
 * multiplicative form's PER-LINE one-nominal-height minimum was itself an
 * under-report: one `OpenGost-TypeB` `‰` at `\\W0.001;` advances almost
 * nothing and still paints 1.19 heights of ink.
 */
function mtextPaintedBound(advanceFactor: number, inkFactor: number, nominalHeight: number): number {
  return nominalHeight * (advanceFactor * MAX_GLYPH_ADVANCE + inkFactor * MAX_GLYPH_INK);
}

/**
 * OCS-local box corners for a single-line TEXT entity (justification-aware): the 4 corners of
 * the DXF-semantic rectangle, PLUS 4 more for the rectangle the mesh paints whenever that is a
 * different rectangle ({@link textMeshLocalCorners}) — so a consumer must treat this as a POINT
 * SET to bound, never as one rectangle's ring.
 */
export interface TextLocalBox {
  readonly corners: readonly Local2[];
}

/**
 * The `insertionPoint`/`alignmentPoint`-anchored, justification-aware OCS-local box of a
 * single-line TEXT (or ATTDEF, which shares the identical structural fields) entity, UNION'd
 * with the rectangle the live mesh really paints — see {@link textMeshLocalCorners} for why
 * those are two different rectangles (#803) and for the frame the union is taken in.
 */
export function textJustifiedLocalBox(e: {
  readonly insertionPoint: Local2;
  readonly alignmentPoint?: Local2;
  readonly height: number;
  readonly rotation: number;
  readonly contents: string;
  readonly widthFactor: number;
  readonly hJustification: number;
  readonly vJustification: number;
  /** DXF group 71 (TEXT/ATTDEF) — bit 2 = BACKWARD (mirror-X), bit 4 = UPSIDE_DOWN (mirror-Y). */
  readonly generationFlags?: number;
  /** DXF group 51 (TEXT/ATTDEF) — the italic-like shear angle, degrees. */
  readonly obliqueAngle?: number;
}): TextLocalBox {
  // @codex T1 arm(c) round-21 P2 finding (F2): `height`/`widthFactor` both use the render path's
  // `positiveOr` fallback (2.5 / 1) instead of a near-zero clamp, and the corrected `height` is
  // used EVERYWHERE below, not just in a width-only local.
  const height = positiveOr(e.height, 2.5);
  const widthFactor = positiveOr(e.widthFactor, 1);
  const width = estimateTextWidth(e.contents ?? "", height, widthFactor);
  const generationFlags = e.generationFlags ?? 0;
  const obliqueAngle = e.obliqueAngle ?? 0;

  // Aligned(3) / Fit(5): baseline runs insertionPoint → alignmentPoint; width + angle come from
  // that EXACT vector (real DXF data), not the heuristic. A DEGENERATE vector (the ends
  // coincide) pins neither, so it keeps `rotation` and the heuristic width.
  const aligned = (e.hJustification === 3 || e.hJustification === 5) && !!e.alignmentPoint;
  const dx = (e.alignmentPoint?.x ?? e.insertionPoint.x) - e.insertionPoint.x;
  const dy = (e.alignmentPoint?.y ?? e.insertionPoint.y) - e.insertionPoint.y;
  const dist = Math.hypot(dx, dy);
  const pinned = aligned && dist > 1e-9;
  const angleDeg = pinned ? (Math.atan2(dy, dx) * 180) / Math.PI : e.rotation;
  const boxWidth = pinned ? dist : width;

  // Otherwise: a non-default justification (h!=0 or v!=0) anchors at the ALIGNMENT point when
  // the source carried one; Left+Baseline (0,0) — and any justified TEXT missing an alignment
  // point — anchors at the INSERTION point (Autodesk DXF reference). Aligned/Fit anchor at the
  // INSERTION point too: it is the FIRST of the two vector ends.
  const anchor =
    !aligned && (e.hJustification !== 0 || e.vJustification !== 0) && e.alignmentPoint
      ? e.alignmentPoint
      : e.insertionPoint;

  // hJustification: 0=Left,1=Center,2=Right,4=Middle(horizontally-centered, like Center — the
  // Autodesk DXF reference documents "Middle" as centered both ways). col: 0=left-anchored,
  // 1=center-anchored,2=right-anchored. Aligned/Fit is left/baseline on its own vector.
  const col = aligned || e.hJustification === 0 ? 0 : e.hJustification === 2 ? 2 : 1;
  // vJustification: 0=Baseline,1=Bottom — both bottom-row-anchored (DXF carries no descender
  // metric to distinguish baseline from the glyph box's true bottom); 2=Middle; 3=Top.
  // @codex T1 arm(c) round-18 P2 finding: hJustification===4 ("Middle") is DISTINCT from
  // "Center" (===1) — per the Autodesk DXF reference it centers the text BOTH ways on the
  // alignment point, NOT gated by vJustification's raw value (source data conventionally
  // carries 0 alongside a true Middle). Forcing the middle row here fixes a Middle TEXT being
  // anchored at the bottom, which under-reported entityBounds by about half the text height.
  const row = aligned ? 0 : e.hJustification === 4 ? 1 : e.vJustification === 3 ? 2 : e.vJustification === 2 ? 1 : 0;

  // Mirror (BACKWARD/UPSIDE_DOWN) + oblique shear applied to the LOCAL (pre-rotation,
  // anchor-relative) corners — @codex T1 arm(c) round-5 P2 finding: both are model fields the
  // live renderer honours as a local-axis flip (`textGeometry.ts::buildTextSceneGeometry`'s
  // `backward`/`upsideDown`), so a mirrored or obliqued TEXT/ATTDEF used to be bounded on the
  // WRONG side of the anchor (mirror) or too narrow (shear).
  const semantic = applyTextMirrorAndOblique(textLocalBoxCorners(boxWidth, height, col, row), generationFlags, obliqueAngle);
  // …UNION'd with what the MESH paints, a DIFFERENT rectangle whenever any of these five terms
  // differ ({@link textMeshLocalCorners} owns the frame and the #803 derivation). All five equal
  // ⇒ one rectangle and a genuine no-op, which is the DEFAULT Left+Baseline TEXT.
  const meshIsSemantic =
    col === 0 && row === 0 && boxWidth === width && angleDeg === e.rotation &&
    anchor.x === e.insertionPoint.x && anchor.y === e.insertionPoint.y;
  const mesh = meshIsSemantic
    ? []
    : textMeshLocalCorners(e.insertionPoint, anchor, angleDeg, e.rotation, width, height, generationFlags, obliqueAngle);
  return { corners: anchorAndRotate(anchor, [...semantic, ...mesh], angleDeg) };
}

/**
 * The rectangle the LIVE single-line TEXT / ATTDEF mesh really paints, expressed in
 * {@link textJustifiedLocalBox}'s own LOCAL frame — anchor-relative and PRE-rotation, so the
 * caller can concatenate it with the DXF-semantic corners and hand both to ONE
 * {@link anchorAndRotate}.
 *
 * # Why a second rectangle exists at all (#803 — the TEXT twin of #801)
 *
 * The mesh IGNORES justification entirely — module header F2 names the three render sites that
 * prove it. Bounding only the semantic rectangle drops the painted ink out of cache-direct
 * culling, INSERT footprints and picking, as the MTEXT group-71 escape did before round 10.
 *
 * # The frame, stated exactly
 *
 * The two rectangles are anchored at DIFFERENT points (`insertion` vs `anchor`) and, for
 * Aligned(3)/Fit(5), rotated by DIFFERENT angles (group 50 vs the insertion→alignment vector's).
 * Writing `M` for the group-71 mirror, `S` for the group-51 shear and `R(θ)` for a CCW rotation:
 * the caller emits `anchor + R(anchorAngle)·local` for every corner it is handed and the mesh
 * corner must land at `insertion + R(meshAngle)·M·S·c`, so this returns
 *
 *     local(c) = R(−anchorAngle)·(insertion − anchor) + R(meshAngle − anchorAngle)·M·S·c
 *
 * which the caller's single rotate-and-translate turns into exactly that. The entity is rotated
 * ONCE, at the end, by the caller — the relative spin here is a change of BASIS between the two
 * rectangles' own frames, not a second application of the entity's rotation.
 *
 * `width`/`height` are the MESH's: always the heuristic {@link estimateTextWidth} column and
 * `positiveOr(height, 2.5)`, never the Aligned/Fit insertion→alignment `dist`, which pins the
 * SEMANTIC box's width only (the renderer does no fitting). That is also why the union is NOT a
 * no-op on the Aligned/Fit branch — `dist` is real DXF data and the heuristic column an
 * estimate of the same run, so they differ except by coincidence.
 */
function textMeshLocalCorners(
  insertion: Local2, anchor: Local2, anchorAngleDeg: number, meshAngleDeg: number,
  width: number, height: number, generationFlags: number, obliqueAngleDeg: number,
): Local2[] {
  const d = rotateDeg2D(insertion.x - anchor.x, insertion.y - anchor.y, -anchorAngleDeg);
  const spin = meshAngleDeg - anchorAngleDeg;
  const local = applyTextMirrorAndOblique(textLocalBoxCorners(width, height, 0, 0), generationFlags, obliqueAngleDeg);
  return local.map((c) => {
    const r = rotateDeg2D(c.x, c.y, spin);
    return { x: d.x + r.x, y: d.y + r.y };
  });
}

/**
 * Apply the group-51 oblique SHEAR (`x' = x + y·tan(oblique)`, the standard italic-shear
 * transform — widens the box horizontally by `height·tan(oblique)` at the sheared edge,
 * matching how an obliqued glyph run's true footprint extends beyond the unsheared rectangle)
 * to the LOCAL (anchor-relative, pre-rotation) box corners, THEN mirror per DXF group 71's
 * BACKWARD (bit 2, mirror the box to the OPPOSITE side of the anchor along local X) /
 * UPSIDE_DOWN (bit 4, mirror along local Y) flags.
 *
 * @codex T1 arm(c) round-17 P2 finding — "Apply mirror and oblique transforms in renderer
 * order". The LIVE renderer bakes the shear into the glyph geometry FIRST, in the text's own
 * unmirrored local frame, THEN applies the mirror as a local-axis `mesh.scale` flip on top
 * (`sceneTextMeshFactories.ts::applyMirrorScale`) — shear-then-mirror. The two compositions
 * are identical whenever `shear === 0` or `mirrorX === mirrorY`, so every previously-covered
 * case stays byte-identical; only "EXACTLY ONE flag set + a nonzero oblique" differs, and it
 * differed by the SIGN of the skew, i.e. by the side of the anchor it extended.
 *
 * The corners it receives already carry the TEXT descender band
 * ({@link textLocalBoxCorners}), so that band shears and mirrors with the rest of the box
 * rather than hanging straight down off a leaning one (@codex PR #794 round 10 P2, finding A).
 */
function applyTextMirrorAndOblique(local: Local2[], generationFlags: number, obliqueAngleDeg: number): Local2[] {
  const mirrorX = (generationFlags & 2) !== 0 ? -1 : 1;
  const mirrorY = (generationFlags & 4) !== 0 ? -1 : 1;
  const shear = Math.tan((obliqueAngleDeg * Math.PI) / 180);
  return local.map((c) => {
    const sx = c.x + c.y * shear;
    const sy = c.y;
    return { x: sx * mirrorX, y: sy * mirrorY };
  });
}

/**
 * The constant the LIVE MTEXT renderer multiplies `nominalHeight ×
 * lineSpacingFactor` by to get the distance between consecutive BASELINES.
 *
 * It is not a free parameter of this module: `sceneTextMeshFactories.ts`
 * (`buildTextEntityMesh` for the instanced SDF path, `buildShxTextMesh` for
 * the stroke path) computes `lineHeight = g.height × g.lineSpacingFactor ×
 * 1.6` and writes `cursorY = -lineHeight × lineIndex` into the per-glyph
 * offset. Any bound that has to know WHERE line `k` was painted must use the
 * SAME number, so it lives here — in the package both the bounds estimator
 * and (via `@dxf-ai-editor/drawing-model`) the renderer read — rather than
 * being written twice. That duplication is exactly how @codex PR #794 round
 * 4 P2 happened: the shear bound derived its multi-line span with no 1.6 at
 * all, so `\Q45;A\PB` at height 2.5 allocated 2.5 units of left lean against
 * the 4 the renderer applies and line 1's vertices sat outside the box.
 */
export const MTEXT_LINE_ADVANCE_FACTOR = 1.6;

/**
 * The MTEXT baseline-to-baseline advance, in OCS-local units — the ONE
 * definition of the renderer's per-line step (see
 * {@link MTEXT_LINE_ADVANCE_FACTOR}). Line `k`'s baseline sits at
 * `-k × mtextLineAdvance(...)` relative to line 0's.
 *
 * Deliberately UNCLAMPED: `sceneTextMeshFactories.ts` receives an already
 * `positiveOr`-clamped `lineSpacingFactor` from `mtextFromEntity.ts` and
 * {@link mtextBoxSize} applies its own `> 0 ? … : 1` clamp before calling
 * here, so folding a clamp in would change one of the two call sites. The
 * multiplication order matches the renderer's expression term for term.
 */
export function mtextLineAdvance(nominalHeight: number, lineSpacingFactor: number): number {
  return nominalHeight * lineSpacingFactor * MTEXT_LINE_ADVANCE_FACTOR;
}

/**
 * The OCS-local vertical extent of an MTEXT's LINE GRID, in world units — the
 * companion to {@link mtextLineAdvance} and the ONE definition both the
 * model-layer box ({@link mtextBoxSize}, as the FLOOR of
 * {@link mtextEstimatedHeightInterval}) and the render-layer AABB
 * (`scene3d/adapters/geom/mtextGeometry.ts`) derive their height from.
 *
 * The GRID, not the painted ink: `[lowest baseline, +1.00 × nominalHeight]`.
 * {@link mtextEstimatedHeightInterval} adds what lives outside it — see
 * "Deliberately NOT covered" (@codex PR #794 round 10).
 *
 * # The derivation
 *
 * In the mesh's own local frame line 0's baseline is at `y = 0` and line
 * `k`'s at `y = -k × ` {@link mtextLineAdvance}
 * (`sceneTextMeshFactories.ts`'s `cursorY = -lineHeight × lineIndex`), and
 * line 0's caps reach `+nominalHeight` — so the grid is `y ∈ [-(lineCount −
 * 1) × advance, +nominalHeight]` and its extent, what this returns, is
 * `nominalHeight + (lineCount − 1) × advance`.
 *
 * # Why the old multiplicative form was wrong, twice over
 *
 * It read `max(nominalHeight, nominalHeight × lineCount × lineSpacingFactor)`
 * — the renderer's 1.6 missing entirely, AND `lineCount` whole line-heights
 * counted where only `lineCount − 1` baseline DROPS plus ONE cap height are
 * painted. The two errors do not cancel: the result converges to `1 / 1.6` of
 * the real span, a 37.5% under-report for a tall block, the whole deficit
 * falling BELOW the insertion point at AutoCAD's default top-left attachment
 * (@codex PR #794 round 4 P2). The additive form also needs no
 * `Math.max(nominalHeight, …)` floor, where the product did at `lineCount
 * === 1` with `lineSpacingFactor < 1`: its first term IS `nominalHeight` and
 * the second is non-negative for every `lineCount ≥ 1` and
 * `lineSpacingFactor > 0`, which both call sites guarantee.
 *
 * # Deliberately NOT covered
 *
 * Everything that is a property of a RUN rather than of the line grid, which
 * is why this function is a FLOOR and not the answer:
 *
 *   * the DESCENT below the lowest baseline — the grid's lower end IS that
 *     baseline, and `textRunRenderer.ts` paints below it;
 *   * inline `\H`, which scales a run's glyphs but never the line step, so
 *     `\H100x;A` paints a cap 100× `nominalHeight` tall and this function
 *     still returns one;
 *   * the `\A` baseline offset, which moves a run's whole painted interval.
 *
 * {@link mtextEstimatedHeightInterval} adds all three from the per-run
 * interval, so {@link mtextBoxSize} DOES cover them (@codex PR #794 round
 * 10). This function stays grid-only on purpose: `mtextGeometry.ts` applies
 * it symmetrically as a render-side AABB and wants the line step, and it is
 * the finite fallback rung the estimate degrades to on an overflow.
 */
export function mtextBlockSpan(
  nominalHeight: number,
  lineCount: number,
  lineSpacingFactor: number,
): number {
  return nominalHeight + (lineCount - 1) * mtextLineAdvance(nominalHeight, lineSpacingFactor);
}

/** The parser's paragraph break, both cases (`mtextParser.ts`'s `pushLineBreak` call sites). */
const PARAGRAPH_CODE = /[Pp]/;
/** The parser's other two-character codes — the three decorations. They flush the run, and scale nothing. */
const DECORATION_CODE = /[LlOoKk]/;
/** The letters `mtextParser.ts` terminates on a `;` — every `\\<letter><arg>;` code it knows. */
const ARG_TERMINATED_CODE = /[HhWwTtQqCcAaSsBbfF]/;
/** A letter after a backslash — the parser's "unknown code" test. */
const LETTER = /[A-Za-z]/;
/**
 * The characters that force the full parser-faithful walk. Anything else is
 * plain text whose painted glyph count is its character count: `\\` opens
 * every inline code, `{` / `}` are the style scope (and paint nothing), and
 * `%` may open a `%%d` / `%%c` / `%%p` / `%%%` escape the parser collapses to
 * ONE glyph.
 */
const NEEDS_PARSER_WALK = /[\\{}%]/;

/**
 * The scale factors the painted-run walk tracks, as ONE scoped state — the subset of `mtextParser.ts`'s
 * `MTextStyleState` that moves painted vertices horizontally. `{` pushes a copy, `}` restores it.
 */
interface InlineScaleState {
  /**
   * `\\H`'s height in force as a FACTOR of the nominal height — THIS walk's own representation, which every
   * geometry field below is built out of; a value underflowed to 0 through repeated `\\H<tiny>x;` included.
   */
  readonly height: number;
  /**
   * The SAME height, in the PARSER'S OWN representation: WORLD units, accumulated in the PARSER'S OWN ORDER
   * (`mtextParser.ts`: `state.heightWorld * val` for `\\H2x;`, a bare `val` for `\\H2;`, the entity's nominal
   * height to begin with). It is deliberately NOT {@link height} × the nominal: THAT conversion — a divide here and
   * a multiply back — is what underflows, overflows and re-associates at the extremes, and it is the whole of
   * @codex PR #794 rounds 19 and 20. Reported through `onRun` so a caller reads what the RENDERER will paint.
   */
  readonly heightWorld: number;
  /**
   * The height a RUN is painted at, not always `height`: the parser's `makeRunFromState` falls back to the entity's
   * own when the state's is not positive (`state.heightWorld > 0 ? … : defaults.heightWorld`).
   */
  readonly runHeight: number;
  /** `\\W` width factor. */
  readonly width: number;
  /** `\\T` letter-tracking factor. */
  readonly tracking: number;
  /** `tan` of the `\\Q` oblique in force — SIGNED, and never clamped. */
  readonly tan: number;
  /**
   * The `\\A` baseline offset in force, as a factor of {@link runHeight} — {@link ALIGNMENT_BASELINE_FACTOR}. It
   * shifts the run's WHOLE painted y-interval, so both ends of the shear interval carry it.
   */
  readonly align: number;
  /**
   * `runHeight × width × tracking` — the renderer's own per-glyph advance multiplier (`textRunRenderer.ts`:
   * `cursorX += glyph.advance × heightScale × widthFactor × trackingFactor`), precomputed once per state change so
   * the per-character hot path stays a single multiply-add.
   */
  readonly advance: number;
  /**
   * `runHeight × max(1, width)` — how far ONE of this run's glyph quads may reach past the pen, in units of
   * {@link MAX_GLYPH_INK}. The `max(1, …)` is there because the two live paths disagree about whether `\\W`
   * stretches the ink, so the bound takes the wider — see that constant.
   */
  readonly ink: number;
}

/** An {@link InlineScaleState} with its three derived fields kept consistent. `heightWorld` is CARRIED, never
 *  derived from {@link height}: re-deriving it is the lossy conversion (@codex PR #794 round 20 P2). */
function scaleState(height: number, heightWorld: number, width: number, tracking: number, tan: number, align: number): InlineScaleState {
  const runHeight = height > 0 ? height : 1;
  return {
    height,
    heightWorld,
    runHeight,
    width,
    tracking,
    tan,
    align,
    advance: runHeight * width * tracking,
    ink: runHeight * Math.max(1, width),
  };
}

/**
 * The glyph count one buffered run paints — its character count AFTER the
 * `%%` escapes `mtextParser.ts` decodes (`%%d` → `°`, `%%c` → `Ø`, `%%p` →
 * `±`, `%%%` → `%`), which collapse three source characters into one glyph.
 * The parser decodes per RUN, after segmentation, so a `%%d` split across a
 * format code is NOT decoded and this is called with the same buffer
 * boundaries the parser flushes at. Re-applied rather than imported for this
 * module family's standing reason (`.dependency-cruiser.cjs` forbids
 * `packages/**` → `apps/web`); the replacement chain is
 * `decodeDxfSpecialChars`'s, in its order, because the order is observable
 * (`%%%d` is `%°`, not `%%` + `d`).
 */
function decodedGlyphCount(buffered: string): number {
  if (!buffered.includes("%")) return buffered.length;
  return buffered.replace(/%%d/gi, "°").replace(/%%c/gi, "Ø").replace(/%%p/gi, "±").replace(/%%%/g, "%").length;
}

/**
 * What an MTEXT's contents actually PAINT, per {@link mtextPaintedLayout}.
 *
 * Every field is derived from runs that put glyphs on the canvas, at the
 * scale those glyphs are painted at. Nothing here is a maximum taken across
 * unrelated runs (see {@link mtextPaintedLayout}'s "Why maxima were not
 * enough").
 */
export interface MtextPaintedLayout {
  /** Paragraphs: the parser's `\\P` / `\\p` / `\n` breaks, plus one. */
  readonly lineCount: number;
  /**
   * The widest line's painted advance, in units of `nominalHeight ×
   * MAX_GLYPH_ADVANCE`: `max over lines of Σ over that line's runs
   * (glyphs × runHeightFactor × widthFactor × trackingFactor)`.
   */
  readonly lineAdvanceFactor: number;
  /**
   * The same maximum with every inline factor at 1 — the plain glyph count of
   * the longest line, for the non-finite fallback rung.
   */
  readonly lineGlyphs: number;
  /**
   * The widest painted run's `runHeightFactor × max(1, widthFactor)`, floored
   * at 1 — the single-glyph INK reach {@link MAX_GLYPH_INK} is applied to.
   *
   * CORRELATED: each run contributes its OWN height times its OWN width
   * factor, never one run's height times another's width. Floored at 1 so a
   * run painted smaller than the entity's nominal height cannot shrink the
   * term below one glyph at the nominal scale.
   */
  readonly inkFactor: number;
  /**
   * The painted y-interval's two ends, in OCS-LOCAL UNITS from LINE 0's
   * baseline — `max over painted runs of aboveWorld_r` and of `belowWorld_r`
   * (a POSITIVE depth), each floored at the line grid's own band
   * ({@link paintedVerticalInterval}). They are the SAME two ends the shear
   * pairs below are `tan_r` times: one interval, read once, used by both
   * axes (@codex PR #794 round 10) — a second maximum over a
   * differently-shaped expression is what produced rounds 7 and 8.
   */
  readonly paintedAbove: number;
  readonly paintedBelow: number;
  /**
   * Signed extremes, in OCS-LOCAL UNITS, of `tan_r × aboveWorld_r` over
   * painted runs — the horizontal displacement the `\\Q` shear gives the TOP
   * end of each run's painted y-interval, at that run's OWN tangent (a tall
   * run and an obliqued run may be different runs — @codex PR #794 round 7
   * P2). `aboveWorld_r = runHeight_r + max(0, offset_r − drop_r)`: normally
   * just the cap height, and more only for a run LIFTED above its own line
   * by `\\A3;` (see {@link ALIGNMENT_BASELINE_FACTOR}).
   */
  readonly shearAboveMin: number;
  readonly shearAboveMax: number;
  /**
   * Signed extremes of `tan_r × belowWorld_r` — the same product at the
   * BOTTOM end. The interval end itself is `-belowWorld_r`, so this field's
   * SIGN is the opposite of the displacement it causes and
   * {@link mtextEstimatedWidth} negates it. `belowWorld_r =
   * MAX_GLYPH_DESCENT × runHeight_r + max(0, drop_r − offset_r)`, where
   * `drop_r` is the run's OWN baseline depth, `lineIndex_r ×`
   * {@link mtextLineAdvance} — not the block's deepest (round 8 P2, finding
   * B) — and `offset_r` its `\\A` shift (round 9 P2). The descent term is why
   * this is non-zero on LINE 0, where the old form left the opposite shear
   * side at exactly zero.
   */
  readonly shearBelowMin: number;
  readonly shearBelowMax: number;
}

/**
 * `Math.max` over a painted REACH, with one difference: a non-finite reach is
 * UNBOUNDED, not ignored. `a > b` is `false` whenever either side is `NaN`,
 * so a plain running maximum silently DROPS a run whose height overflowed —
 * narrowing the box exactly where it is least bounded. Propagating `+∞`
 * instead makes {@link mtextEstimatedHeightInterval}'s finite guard fire and the
 * estimate degrade to the line grid, the same ladder
 * {@link mtextEstimatedWidth} uses for the same inputs.
 */
function maxReach(accumulated: number, reach: number): number {
  if (!Number.isFinite(reach)) return Number.POSITIVE_INFINITY;
  return reach > accumulated ? reach : accumulated;
}

/**
 * The painted y-interval's two ends, from the walk's per-run maxima and the
 * LINE GRID they sit on — the ONE place the grid floor is applied, so the
 * fast path and the parser walk cannot disagree about it.
 *
 * The grid is what an MTEXT occupies with NO inline codes: line 0's nominal
 * cap band, down to the last line's baseline plus one nominal descender
 * band. Runs only push the interval FURTHER out (a `\\H` run is taller, an
 * `\\A` run sits lower), never pull it in — a line painted at half the
 * nominal height still occupies its whole row, because
 * `sceneTextMeshFactories.ts` steps the line cursor by
 * {@link mtextLineAdvance} on the NOMINAL height, and a trailing empty
 * paragraph occupies its own row for the same reason.
 */
function paintedVerticalInterval(
  runAbove: number,
  runBelow: number,
  nominalHeight: number,
  lineCount: number,
  lineAdvanceWorld: number,
): { paintedAbove: number; paintedBelow: number } {
  return {
    paintedAbove: maxReach(runAbove, nominalHeight),
    paintedBelow: maxReach(runBelow, MAX_GLYPH_DESCENT * nominalHeight + (lineCount - 1) * lineAdvanceWorld),
  };
}

/**
 * What `contents` PAINTS: per line, the sum over its runs of the advance each run really
 * contributes, plus the paragraph count, the tallest painted run and the `\\Q` lean — everything
 * {@link mtextEstimatedWidth} and {@link mtextBoxSize} need, from ONE parser-faithful walk.
 *
 * # Why this exists
 *
 * The renderer advances its cursor once per PAINTED glyph, by that glyph's own run's factors
 * (`textRunRenderer.ts`: `cursorX += glyph.advance × heightScale × widthFactor × trackingFactor`,
 * `heightScale = run.heightWorld / atlas.lineHeight`), and starts a new cursor at each paragraph
 * run. A line's painted width is therefore a SUM over its runs, and this walk reproduces exactly
 * that sum.
 *
 * # Why maxima over a raw character split were not enough
 *
 * The estimate used to be `longestLineChars × maxHeight × maxWidth ×
 * maxTracking` over a RAW `/\\P|\n/` split, and @codex PR #794 round 7 found
 * both halves wrong. The maxima are NOT CORRELATED (finding B): taken over the whole entity they
 * synthesise a scale no glyph is painted at — `{\\H1000000x;A}{\\W1000000;B}` paints 3e6 units at a
 * 2.5 nominal while the product of the two global maxima times 27 raw characters reported 4.05e13,
 * the multiplicative over-report that blanks the canvas ({@link mtextBoxSize}'s title-block
 * regression). And the raw split CANNOT SEE AN ESCAPE (finding A): the regex matches the second
 * backslash of an escaped `\\\\P` too — the parser paints `\P\P` as four glyphs on ONE line where
 * the split saw three lines — and it missed `\\p`, which the parser DOES break on.
 *
 * Summing per run fixes both, and is STRICTLY the painted truth: format-code characters no longer
 * enter the count at all. That is a real narrowing (`\\H100x;A` is eight source characters and ONE
 * glyph) and it is the point — the old excess was not a safety margin, it was the thing a scale
 * multiplied to blow the box up.
 *
 * # Why it is re-derived instead of imported
 *
 * `mtextParser` lives in `apps/web`; `packages/drawing-model` cannot import it
 * (`.dependency-cruiser.cjs` forbids the direction). The walk therefore mirrors the parser's OWN
 * rules rather than inventing different ones, and `mtextInlineScaleParity.test.ts` (in `apps/web`,
 * the one side that may import both) pins the agreement against `parseMTextContent` itself over a
 * generated grammar. Mirroring means its REAL semantics, not idealised ones:
 *
 *   * The style state is SCOPED: `{` pushes a copy and `}` restores it. An earlier revision carried
 *     the running height ACROSS group boundaries, so `{\\H100x;A}{\\H100x;B}` — two independent
 *     100× runs — compounded to 10 000×, and N groups to `100^N` (@codex PR #794 round 6 P2).
 *   * `}` with an EMPTY stack is a no-op — it does not reset to the defaults.
 *   * There is NO `\\{` / `\\}` escape: the parser's `\\` branch only special-cases `\\\\`, and for
 *     any other non-letter buffers the BACKSLASH as a glyph and advances ONE character, so the
 *     brace still scopes.
 *   * `\\P` / `\\p` / `\n` break the line but paint nothing, and — unlike a group — restore
 *     nothing: a style opened in one paragraph carries into the next.
 *   * `\\S<num><sep><den>;` PAINTS: one run of `num` + `/` (or `±` for the `#` tolerance form) +
 *     `den`. With NO separator the payload is buffered as plain text instead; an empty payload
 *     (`\\S;`) paints nothing.
 *   * Every code that CHANGES the style flushes the buffered run first, and `\\H` / `\\W` / `\\T` /
 *     `\\Q` / `\\C` / `\\A` flush only when their argument is ACCEPTED (`Number.isFinite(v) && v >
 *     0` for the first three; `Number.isFinite(v)` alone for `\\Q`, which takes zero and negative
 *     degrees; `0 ≤ v ≤ 3` for `\\A`; an INTEGER in `[1, 255]` for `\\C`, i.e. `aciToHex(n) !==
 *     null`). That boundary is observable through {@link decodedGlyphCount} alone — a `%%d`
 *     straddling a code is not decoded — which is why it is mirrored at all.
 *   * The decorations `\\L` / `\\O` / `\\K` (and lowercase) flush unconditionally and scale
 *     nothing, as do `\\C` / `\\A` / `\\B` / `\\f` / `\\F`, and are matched BEFORE the
 *     `;`-terminated codes exactly as the parser matches them — otherwise `\\L\\H7;A` reads as an
 *     unknown code running to the `\\H`'s terminator and the height is lost.
 *   * `\\f`'s Q29.A embedded-block form emits an EMPTY-text marker run, so it paints no glyph here.
 *     Unbounded in principle (a block is scaled to the run height at an arbitrary aspect) but
 *     latent — no production `parseMTextContent` call site passes `blockDefinitions`.
 *   * An unterminated `\\H`/`\\W`/`\\T`/`\\Q`/`\\C`/`\\A`/`\\S`/`\\B`/`\\f` (no `;` anywhere after
 *     it) falls through to the parser's unknown-code branch, which consumes `;`-terminated codes
 *     only while the terminator is within 40 characters and otherwise drops just the two-character
 *     `\\<letter>` pair. The unknown-code branch does NOT flush.
 *
 * `\\H` keeps the two forms the parser distinguishes: `\\H2x;` is RELATIVE — it multiplies the
 * height in force — and `\\H2;` is ABSOLUTE, replacing it with a world value, which becomes a
 * factor here by dividing by the nominal height (and carries no scale information at all when that
 * is non-positive).
 *
 * `onRun` reports each PAINTED run's OWN world height, in emit order, and is the only per-RUN channel out of this
 * walk: every returned field is an AGGREGATE — a maximum over runs, a longest-line sum or the paragraph count — so a
 * run that is neither the widest nor the tallest is invisible in all of them, which is exactly how an absolute-`\\H`
 * run hid from `mtextScaleSimilarity.ts` (@codex PR #794 round 17 P2). Its SECOND argument is the SAME run's height
 * as the PARSER computes it — {@link InlineScaleState.heightWorld} under the parser's own `> 0` fallback — because
 * the first is this walk's factor times the nominal and the two part company wherever that conversion is
 * ill-conditioned (rounds 19 and 20 P2). The bounds path passes nothing.
 */
export function mtextPaintedLayout(contents: string, nominalHeight: number, lineSpacingFactor: number, onRun?: (heightWorld: number, parserHeightWorld: number) => void): MtextPaintedLayout {
  // The renderer's OWN per-line step. It is needed HERE, and not at the two multiplications it used
  // to live at, because a run's painted y-interval now mixes a run-height term (the cap, the descent,
  // the `\A` offset) with a line-advance term (its baseline depth) — and the maximum over runs of a SUM
  // cannot be decomposed into maxima of the parts without pairing one run's descent against another
  // run's drop. Evaluating each run's interval in OCS-local units at the point of emit keeps every term
  // correlated with the run that carries it, the invariant rounds 7 and 8 were both about (round 9 P2).
  const lineAdvanceWorld = mtextLineAdvance(nominalHeight, lineSpacingFactor);
  // Fast path: no backslash, brace or `%` ⇒ every character is one glyph at
  // the nominal scale. This runs for every MTEXT in the document on every
  // bounds pass, and the overwhelming majority of MTEXT contents carry no
  // formatting at all.
  if (!NEEDS_PARSER_WALK.test(contents)) {
    const lines = contents.split("\n");
    let longest = 0;
    for (const line of lines) if (line.length > longest) longest = line.length;
    const lineCount = Math.max(1, lines.length);
    return {
      lineCount,
      lineAdvanceFactor: longest,
      lineGlyphs: longest,
      inkFactor: 1,
      // No inline code can have moved either end, so the interval IS the
      // grid — the same expression the walk below floors its per-run maxima
      // at, not a second formula.
      ...paintedVerticalInterval(0, 0, nominalHeight, lineCount, lineAdvanceWorld),
      shearAboveMin: 0,
      shearAboveMax: 0,
      shearBelowMin: 0,
      shearBelowMax: 0,
    };
  }

  let state = scaleState(1, nominalHeight, 1, 1, 0, 0);
  const stack: InlineScaleState[] = [];
  let buffer = "";
  let lineCount = 1;
  let lineAdvance = 0;
  let lineGlyphCount = 0;
  let lineAdvanceFactor = 0;
  let lineGlyphs = 0;
  let inkFactor = 1;
  // SIGNED extremes, not `max |tan|`: the shear's DIRECTION decides which side of the layout box the
  // painted vertices land on, and a run may legally carry both signs (`\\Q20;A\\Q-20;B`), in which case
  // it really does lean both ways. `min ≤ 0 ≤ max` by construction for both pairs (the no-code case is
  // the zero shear), so a single-signed run leaves the other side at exactly 0 and the bound reduces
  // to the one-sided case.
  //
  // BOTH pairs are correlated with the run that carries the lean: each is that run's OWN tangent times
  // one END of that run's OWN painted y-interval, evaluated in OCS-local units right here so no maximum
  // is ever taken across a mix of units. Neither is the entity-wide `tan` extreme any more (@codex PR
  // #794 round 7 P2 for the cap end, round 8 P2 finding B for the baseline end, round 9 P2 for the
  // descent and the `\A` offset that end carries).
  let shearAboveMin = 0;
  let shearAboveMax = 0;
  let shearBelowMin = 0;
  let shearBelowMax = 0;
  // The SAME two interval ends, UNSHEARED — the height axis's whole input (@codex PR #794 round 10),
  // accumulated from the identical `aboveWorld` / `belowWorld` the four fields above are multiplied
  // by, in the same loop iteration, so the vertical box and the horizontal lean cannot disagree.
  let runAbove = 0;
  let runBelow = 0;

  /** One PAINTED run of `glyphs` glyphs at the live state. */
  const emit = (glyphs: number): void => {
    if (glyphs <= 0) return;
    lineAdvance += glyphs * state.advance;
    lineGlyphCount += glyphs;
    if (state.ink > inkFactor) inkFactor = state.ink;
    // This run's painted y-interval, in OCS-local units from LINE 0's baseline — the union of what
    // the two live paths shear (see {@link mtextEstimatedWidth}'s "which side" section). Four terms, all
    // this run's own: `runHeightWorld`, its cap reach above its own baseline; `dropWorld`, its baseline's
    // depth (`lineCount` counts breaks + 1, so this run sits on line `lineCount − 1` and line 0 gives
    // exactly 0 — @codex round 8 P2, finding B); `offsetWorld`, its `\A` shift, which moves BOTH ends;
    // and the DESCENT (round 9 P2 — without it the bottom end was 0 on line 0 and the shear side it
    // feeds vanished). Computed BEFORE the non-finite-tangent bail-out (round 10): an overflowed `\Q`
    // costs the run its LEAN, not its HEIGHT — bailing out first left `\Q1e308;\H100x;A` reporting one
    // nominal height for a run painted a hundred of them tall.
    const runHeightWorld = state.runHeight * nominalHeight;
    // The per-run channel, reported HERE — the one point at which a run's world height exists, before any maximum has
    // folded it away. Every other per-run quantity the walk derives (`state.advance`, `state.ink`, `offsetWorld`, and
    // the two shear products) is the FIRST number times a factor that does not depend on `nominalHeight` at all, so
    // it is this walk's whole scale behaviour. The SECOND is the PARSER's own — `makeRunFromState`'s
    // `state.heightWorld > 0 ? state.heightWorld : defaults.heightWorld`, verbatim — i.e. what is actually painted.
    onRun?.(runHeightWorld, state.heightWorld > 0 ? state.heightWorld : nominalHeight);
    const offsetWorld = state.align * runHeightWorld;
    const dropWorld = (lineCount - 1) * lineAdvanceWorld;
    const aboveWorld = runHeightWorld + Math.max(0, offsetWorld - dropWorld);
    const belowWorld = MAX_GLYPH_DESCENT * runHeightWorld + Math.max(0, dropWorld - offsetWorld);
    runAbove = maxReach(runAbove, aboveWorld);
    runBelow = maxReach(runBelow, belowWorld);
    if (!Number.isFinite(state.tan)) {
      // A non-finite tangent bounds nothing; propagate it so
      // `mtextEstimatedWidth`'s finite guard falls back to the SAFE
      // (over-reporting) side instead of silently returning a narrow box.
      // Sign-less infinity: poison BOTH sides of BOTH pairs, since either
      // could be the one that overflowed.
      shearAboveMin = Number.NEGATIVE_INFINITY;
      shearAboveMax = Number.POSITIVE_INFINITY;
      shearBelowMin = Number.NEGATIVE_INFINITY;
      shearBelowMax = Number.POSITIVE_INFINITY;
      return;
    }
    const above = state.tan * aboveWorld;
    if (above > shearAboveMax) shearAboveMax = above;
    if (above < shearAboveMin) shearAboveMin = above;
    const below = state.tan * belowWorld;
    if (below > shearBelowMax) shearBelowMax = below;
    if (below < shearBelowMin) shearBelowMin = below;
  };

  /** The parser's `flushBuffer()`: a non-empty buffer becomes one run at the live state. */
  const flushBuffer = (): void => {
    if (buffer.length === 0) return;
    emit(decodedGlyphCount(buffer));
    buffer = "";
  };

  /** The parser's `pushLineBreak()`: flush, then start a new line's cursor. */
  const breakLine = (): void => {
    flushBuffer();
    if (lineAdvance > lineAdvanceFactor) lineAdvanceFactor = lineAdvance;
    if (lineGlyphCount > lineGlyphs) lineGlyphs = lineGlyphCount;
    lineAdvance = 0;
    lineGlyphCount = 0;
    lineCount += 1;
  };

  let i = 0;
  while (i < contents.length) {
    const ch = contents[i]!;

    // ── `{` / `}` — the parser's style scope ────────────────────────────
    if (ch === "{") {
      flushBuffer();
      stack.push(state);
      i += 1;
      continue;
    }
    if (ch === "}") {
      flushBuffer();
      const popped = stack.pop();
      // An UNBALANCED `}` is a no-op in the parser (`pop()` gives `undefined` and the state is left as it is) — it
      // does NOT reset. The whole state travels on this stack, `heightWorld` included (rounds 19 and 20 P2).
      if (popped !== undefined) state = popped;
      i += 1;
      continue;
    }

    if (ch !== "\\") {
      // A literal newline is a paragraph break (the parser's `pushLineBreak`),
      // so it paints nothing; every other character is buffered as a glyph.
      if (ch === "\n") breakLine();
      else buffer += ch;
      i += 1;
      continue;
    }

    const next = contents[i + 1];
    if (next === undefined) {
      // Trailing lone backslash: the parser buffers it as a literal glyph.
      buffer += "\\";
      i += 1;
      continue;
    }
    // `\\\\` — one literal backslash glyph, and the reason an ESCAPED
    // backslash can never make the following text look like a code.
    if (next === "\\") {
      buffer += "\\";
      i += 2;
      continue;
    }
    if (PARAGRAPH_CODE.test(next)) {
      breakLine();
      i += 2;
      continue;
    }
    if (DECORATION_CODE.test(next)) {
      flushBuffer();
      i += 2;
      continue;
    }

    // ── `\\<letter><arg>;` — every code the parser terminates on `;` ─────
    if (ARG_TERMINATED_CODE.test(next)) {
      const semi = contents.indexOf(";", i + 2);
      if (semi !== -1) {
        const arg = contents.slice(i + 2, semi);
        if (next === "H" || next === "h") {
          const isMultiplier = arg.endsWith("x") || arg.endsWith("X");
          const value = Number(isMultiplier ? arg.slice(0, -1) : arg);
          if (Number.isFinite(value) && value > 0) {
            flushBuffer();
            // RELATIVE (`\\H2x;`) multiplies the height in force; ABSOLUTE (`\\H2;`) replaces it. The FACTOR form this
            // walk's own geometry runs on divides an absolute value by the nominal one — which carries no scale
            // information when it is itself non-positive. The PARSER's WORLD form is carried BESIDE it, in the
            // parser's own order and with no division at all (@codex PR #794 rounds 19 and 20 P2), so the two never
            // have to be converted into one another: it is that CONVERSION, and neither chain, that underflows to 0,
            // overflows to ∞ and re-associates at the extremes. STICKY and `{`/`}`-scoped for free, being state.
            const height = isMultiplier ? state.height * value : nominalHeight > 0 ? value / nominalHeight : state.height;
            const heightWorld = isMultiplier ? state.heightWorld * value : value;
            state = scaleState(height, heightWorld, state.width, state.tracking, state.tan, state.align);
          }
        } else if (next === "W" || next === "w") {
          const value = Number(arg);
          if (Number.isFinite(value) && value > 0) {
            flushBuffer();
            state = scaleState(state.height, state.heightWorld, value, state.tracking, state.tan, state.align);
          }
        } else if (next === "T" || next === "t") {
          const value = Number(arg);
          if (Number.isFinite(value) && value > 0) {
            flushBuffer();
            state = scaleState(state.height, state.heightWorld, state.width, value, state.tan, state.align);
          }
        } else if (next === "Q" || next === "q") {
          // `mtextParser.ts`'s `\\Q` branch: ANY finite degrees (zero and negative included — unlike `\\H`/`\\W`/`\\T`
          // there is no `> 0` guard), and both renderers take `Math.tan` of it UNCLAMPED. Mirror that exactly: a
          // clamp the renderer does not have would under-report the very glyphs an unclamped shear throws off to the
          // side. The SIGN is kept because it picks the side — see {@link mtextEstimatedWidth}'s "which side".
          const degrees = Number(arg);
          if (Number.isFinite(degrees)) {
            flushBuffer();
            state = scaleState(state.height, state.heightWorld, state.width, state.tracking, Math.tan((degrees * Math.PI) / 180), state.align);
          }
        } else if (next === "C" || next === "c") {
          // Scales nothing — but the parser flushes when the index MAPS, and
          // the flush is a run boundary `%%` decoding can see.
          const aci = Number(arg);
          if (Number.isInteger(aci) && aci >= 1 && aci <= 255) flushBuffer();
        } else if (next === "A" || next === "a") {
          // `mtextParser.ts`'s `\\A` branch, term for term: any finite `0 ≤ v ≤ 3`, flushed first and stored as
          // `Math.round(v)` (so `2.5` really is alignment 3). It used to be flush-only here, leaving the run's
          // baseline OFFSET out of the interval entirely (@codex PR #794 round 9 P2).
          const value = Number(arg);
          if (Number.isFinite(value) && value >= 0 && value <= 3) {
            flushBuffer();
            const align = ALIGNMENT_BASELINE_FACTOR[Math.round(value)] ?? 0;
            state = scaleState(state.height, state.heightWorld, state.width, state.tracking, state.tan, align);
          }
        } else if (next === "S" || next === "s") {
          // A stacked fraction is a RUN: the parser emits `num`, the separator rendered as `/` (or `±` for the `#`
          // tolerance form) and `den` as ONE run's text at the live state. With no separator the payload is buffered
          // as plain text instead, and an empty payload (`\\S;`) emits nothing at all.
          let sepAt = -1;
          let separator = "^";
          for (let j = 0; j < arg.length; j += 1) {
            const c = arg[j]!;
            if (c === "^" || c === "/" || c === "#") {
              sepAt = j;
              separator = c;
              break;
            }
          }
          flushBuffer();
          if (sepAt >= 0) emit(decodedGlyphCount(`${arg.slice(0, sepAt)}${separator === "#" ? "±" : "/"}${arg.slice(sepAt + 1)}`));
          else buffer += arg;
        } else {
          // `\\B` / `\\f` / `\\F`: consumed, painting nothing and scaling nothing (`\\f`'s embedded-block marker run is
          // empty-text too), and flushing unconditionally once the terminator is found.
          flushBuffer();
        }
        i = semi + 1;
        continue;
      }
      // No terminator anywhere: the parser falls through to its unknown-code branch below, exactly as this does.
    }

    // ── Unknown letter code — strip up to a NEARBY `;`, else the pair ────
    if (LETTER.test(next)) {
      const semi = contents.indexOf(";", i + 2);
      i = semi !== -1 && semi - i < 40 ? semi + 1 : i + 2;
      continue;
    }

    // Non-letter after the backslash (`\\{`, `\\}`, `\\1`, …): the parser has NO such escape — it buffers the
    // BACKSLASH as a literal glyph and advances one character, so the brace that follows is still a scope operator
    // and is handled by this loop's `{` / `}` arms on the next turn.
    buffer += "\\";
    i += 1;
  }

  flushBuffer();
  if (lineAdvance > lineAdvanceFactor) lineAdvanceFactor = lineAdvance;
  if (lineGlyphCount > lineGlyphs) lineGlyphs = lineGlyphCount;

  return {
    lineCount,
    lineAdvanceFactor,
    lineGlyphs,
    inkFactor,
    ...paintedVerticalInterval(runAbove, runBelow, nominalHeight, lineCount, lineAdvanceWorld),
    shearAboveMin,
    shearAboveMax,
    shearBelowMin,
    shearBelowMax,
  };
}

/**
 * How far an MTEXT's painted box OVERHANGS its upright layout column on each
 * side, in OCS-local units, both `≥ 0` — the SIDE extents of the box whose
 * total size is `width`. Two terms reach them, and neither is symmetric:
 * the one-sided enlargement a `\\Q` shear adds (a single-signed run leaves
 * the other side at exactly 0), and {@link MAX_GLYPH_LEFT_BEARING}, the half
 * of {@link MAX_GLYPH_INK} a glyph paints BEFORE its own cursor, which is on
 * the left for every run.
 *
 * They are reported ALONGSIDE `width` rather than folded into it because
 * `width` is a SIZE and the attachment decides where that size is placed — a
 * magnitude cannot say which side the ink landed on. `width` already
 * CONTAINS both (the shear as `width = uprightWidth + overhangLeft +
 * overhangRight`, the bearing inside the ink term), so a consumer that
 * ignores them keeps today's box SIZE; one that honours them puts that
 * extent where the renderer paints it. Renamed from `MtextShearExtents` in
 * round 10: a name saying `shear` hid the bearing from every consumer
 * (@codex PR #794 round 10 P2, finding B).
 */
export interface MtextSideExtents {
  readonly overhangLeft: number;
  readonly overhangRight: number;
}

/**
 * Both sides at zero — the ONE literal every branch that reports no side
 * extent spreads, so "no overhang" cannot be spelled two ways.
 */
const NO_OVERHANG: MtextSideExtents = { overhangLeft: 0, overhangRight: 0 };

/**
 * The rectangle the MTEXT glyph MESH paints, as its four `≥ 0` reaches from
 * the PEN ORIGIN — line 0's baseline at the layout column's `x = 0`. In OCS-
 * local units: `paintedLeft`/`paintedRight` out to the sides, `paintedAbove`/
 * `paintedBelow` up and down, i.e. the local rect `x ∈ [−paintedLeft,
 * +paintedRight]`, `y ∈ [−paintedBelow, +paintedAbove]`.
 *
 * It is carried beside the scalar `width`/`height` because those ALONE cannot
 * say where the mesh puts them: `sceneTextMeshFactories.ts` anchors every
 * MTEXT at line 0's baseline and ignores group 71 entirely, so
 * {@link mtextAnchoredBoxCorners} has to place a second, baseline-anchored
 * rectangle and union it with the group-71 one (@codex PR #794 round 10 P2,
 * finding C). Threaded from {@link MtextPaintedLayout}'s own interval rather
 * than re-derived, because a second derivation of the same ends is the
 * correlation defect rounds 7 and 8 were about.
 *
 * # It is its OWN rectangle, NOT the semantic one re-anchored
 *
 * Round 10 reported the pair as a SPLIT of `height` (`paintedAbove +
 * paintedBelow === height`) and let the corner builder take the sides off
 * {@link MtextSideExtents}. That is false exactly where a group 42/43 is
 * carried: those groups are AutoCAD's own computed DXF box and the live
 * renderer never reads them, so its baseline layout still paints
 * {@link MAX_GLYPH_LEFT_BEARING} left of the pen and {@link MAX_GLYPH_DESCENT}
 * below the baseline, outside the `[0, w42] × [0, h43]` the fast path used to
 * report (@codex PR #794 round 11 P2, finding 1). The four reaches are
 * therefore INDEPENDENT of `width`/`height` and come from the CONTENTS WALK
 * on every branch — round 11's one-line BANDS were far too shallow for a
 * multi-paragraph block (round 12 P2, header F1). The semantic rectangle
 * keeps AutoCAD's numbers verbatim; the two are unioned, so neither
 * statement is lost.
 *
 * # Two right edges, because BOUNDS and SELECT are not the same question
 *
 * `paintedRight` is the WALKED worst case — {@link MAX_GLYPH_ADVANCE} per
 * glyph, 1.22 heights against a real bundled-prose average nearer 0.5. That
 * is right for a PREFILTER, which may over-report. It is wrong for an EXACT
 * crossing ring, which may not: on the reviewer's 36-glyph witness at the
 * 2.5 nominal the walk reaches 112.785 while the atlases paint 26-31 and
 * group 42 ends at 50, so a crossing window in the empty strip beyond 50
 * SELECTED the MTEXT (@codex PR #794 round 13 P2). `selectPaintedRight` is
 * therefore the same walk CLAMPED BY GROUP 42 wherever group 42 is carried
 * — AutoCAD's own measurement of the same string, which still covers the
 * painted ink with room to spare — and is `paintedRight` itself on the
 * estimate branch, where the walk is the only number that exists. It is a
 * `Math.min` of two numbers already in hand, NOT a second derivation of the
 * interval (the rounds-7/8 correlation rule). The X axis alone gets this:
 * see the module header's F1 for why group 43 may not do the same to Y.
 */
export interface MtextPaintedExtents extends MtextSideExtents {
  readonly paintedAbove: number;
  readonly paintedBelow: number;
  readonly paintedLeft: number;
  readonly paintedRight: number;
  readonly selectPaintedRight: number;
}

/** {@link mtextEstimatedWidth}'s result — the width plus its side split. */
interface MtextEstimatedWidth extends MtextSideExtents {
  readonly width: number;
}

/**
 * The FINITE upper bound on an MTEXT's painted width when the writer carried no group 42 — the
 * whole of {@link mtextBoxSize}'s estimate branch.
 *
 * # The two terms — and the one that is deliberately ABSENT
 *
 * 1. **Upright content estimate.** The widest LINE's painted advance plus one glyph's ink overhang
 *    ({@link mtextPaintedBound} over {@link mtextPaintedLayout}): the sum over that line's runs of
 *    `glyphs × runHeight × widthFactor × tracking × MAX_GLYPH_ADVANCE` — the renderer's own cursor
 *    advance, term for term — plus `inkFactor × MAX_GLYPH_INK`. It is an upper bound on an
 *    UNWRAPPED, UNSHEARED layout because glyphs cannot advance past their own run and no quad
 *    reaches more than one `MAX_GLYPH_INK` past the pen, and it is CORRELATED: each run contributes
 *    at the scale its own glyphs are painted at, never at another run's (@codex PR #794 round 7 P2
 *    — see {@link mtextPaintedLayout} for the 1e13 case the old global maxima produced).
 *
 *    Both metrics are BOUNDS measured over the bundled atlases, not the 0.6 AVERAGE the TEXT
 *    estimate uses (@codex PR #794 round 8 P2, finding A). {@link estimateTextWidth} is
 *    deliberately NOT called per run: its `Math.max(height, …)` floor is a PER-LINE minimum
 *    (summing it per run would add a whole height per run), its `Math.max(widthFactor, 1e-6)` guard
 *    is for TEXT's raw entity field, and its metric is the average. The ink term replaces that
 *    floor, once per line.
 * 2. **The group-41 column is NOT a term.** It is the DXF *reference rectangle* — the column
 *    AutoCAD would wrap at. Nothing in this repo wraps, so it describes no geometry we paint and
 *    may not enter the width in any role, cap included. Verified end to end at this commit:
 *    `mtextParser.ts` emits a `lineBreak` run at `\\P` / `\\p` / `\n` and at NOTHING else; both
 *    layout walkers in `sceneTextMeshFactories.ts` advance `lineIndex` only on those paragraph
 *    runs; and the render path never even RECEIVES the column (the `mtext` `SceneGeometry` carries
 *    no reference width, so `mtextGeometry.ts` could not wrap if it wanted to). The PDF converter
 *    records the same absence. A `Math.min(column, estimate)` therefore UNDER-reported whenever the
 *    column was the narrower of the two (@codex PR #794 round 5 P2), while the over-report that cap
 *    was introduced to kill — group 41 = 210 713 on a 10-character title-block label, see {@link
 *    mtextBoxSize} — dies STRUCTURALLY by not consulting group 41 at all. Real wrapping stays open
 *    (U4, #798); it moves painted geometry, so a bounds fix may not assume it.
 * 3. **The oblique shear extent.** `\\Q` is a POST-layout vertex transform, `x' = x +
 *    y·tan(oblique)` — applied per glyph vertex in `shxRunRenderer.ts` (`wx = (gx × widthFactor +
 *    gy × tanOblique) × heightScale + cursorX`) and in the instanced path's vertex shader
 *    (`textShaders.glsl.ts`: `textLocal.x += textLocal.y * shear`). It moves no pen; it displaces
 *    each vertex horizontally in proportion to its HEIGHT above the shear origin. So it adds, on
 *    top of the upright estimate of (1), a displacement `y × tan(oblique)` per vertex — SIGNED, and
 *    therefore landing on ONE side, not spread symmetrically.
 *
 * # Which side, and how far: the `y` interval the shear acts over
 *
 * Both live paths shear about LINE 0's baseline, and the bound must cover BOTH:
 *
 *   * SHX strokes shear glyph-local `y` (baseline-relative, scaled by `heightScale = heightWorld /
 *     font.above`) and add `cursorYBase` only AFTERWARDS (`shxRunRenderer.ts`: `wx = (sx + seg.y ×
 *     tanOblique) × heightScale + cursorX`, `wy = seg.y × heightScale + cursorYBase`), so EVERY
 *     line shears about its own baseline over `y ∈ [-descent, runHeight]` — and the `\\A` offset,
 *     riding in `cursorYBase`, is NOT sheared on this path while the instanced one shears it.
 *   * The instanced SDF path shears the COMBINED local `y` (`textShaders.glsl.ts`: `textLocal =
 *     instance_offset + instance_scale × unitCorner; textLocal.x += textLocal.y × shear`) and
 *     `sceneTextMeshFactories.ts` writes `cursorY = -lineHeight × lineIndex` into that offset, so
 *     line `k` shears about a baseline `k` rows BELOW line 0 and is displaced OPPOSITE line 0's
 *     caps.
 *
 * The covering interval is therefore, PER RUN,
 *
 *     y ∈ [-belowWorld_r, +aboveWorld_r]
 *     belowWorld_r = MAX_GLYPH_DESCENT × runHeight_r + max(0, drop_r − offset_r)
 *     aboveWorld_r = runHeight_r                     + max(0, offset_r − drop_r)
 *
 * — `drop_r` being the depth of the baseline THAT run sits on and `offset_r` its `\\A` shift — and
 * the two ends take their tangent from the run that carries the lean:
 *
 *   * The `+runHeight` end is a GLYPH's own cap, so the lean there is the lean of the run that
 *     paints it: `max over painted runs of tan_r × runHeight_r` ({@link MtextPaintedLayout}'s
 *     `shearRunMin`/`shearRunMax`), NOT the global `max tan × max height` — which would invent a
 *     lean no glyph has (`\\Q89;A{\\H1000x;B}` leans one 2.5-unit cap, not a 2500-unit one). Term
 *     (1)'s correlation defect (@codex round 7 P2).
 *   * The `-drop` end is not a glyph height at all: it is the DISTANCE from line 0's baseline down
 *     to the baseline the leaning run sits on, and the shear that acts over it is the one in force
 *     on the run painted THERE. The step is {@link mtextLineAdvance} — read off the renderer's own
 *     per-line step, NOT re-derived: the old `max(nominalHeight, nominalHeight × lineCount ×
 *     lineSpacingFactor) × maxInlineHeightFactor − runHeight` conflated the block's total SPAN with
 *     the distance to the lowest BASELINE and omitted the renderer's 1.6, leaving the whole of line
 *     1 of `\\Q45;A\\PB` painted 1.5 units outside the box (@codex PR #794 round 4 P2).
 *
 *     The line INDEX is the run's own, not the block's last. Pairing an entity-wide tangent with
 *     the entity-wide depth synthesised displacement no rendered run has: `{\\Q89;A}\\P` + 500
 *     upright `B` lines reported a ~114 000-unit lean at a 2.5 nominal, though the only obliqued
 *     run is on line 0 and leans one 2.5-unit cap (143 units) — the canvas-blanking over-report of
 *     {@link mtextBoxSize}'s title-block regression, reached from ordinary formatted text (@codex
 *     PR #794 round 8 P2, finding B). Taking the max over runs of `max(tan_r × aboveWorld_r, −tan_r
 *     × belowWorld_r)` — rather than of the two ends separately — is what keeps BOTH renderers
 *     covered.
 *   * The DESCENT term is why the bottom end is non-zero on LINE 0 — the whole of @codex PR #794
 *     round 9 P2. `drop_0` is 0, so the interval used to be `[0, runHeight_0]` there and one of the
 *     two signed sides came out EXACTLY zero for every single-line obliqued run, while the renderer
 *     paints below the baseline and shears what it paints there: `\\Q89;g` leans its cap 57.29
 *     heights RIGHT and throws the `g`'s tail 12.5 heights LEFT in the bundled Roboto atlas, and
 *     `shearLeft` was 0.
 *   * The `\\A` OFFSET moves the whole interval, so it appears at both ends with opposite signs.
 *     `\\A2;` sinks a run by a whole run height and `\\A3;` lifts it by half — the one input that
 *     makes `aboveWorld_r` exceed `runHeight_r`.
 *
 * TWO deliberate consequences of taking the renderer's formula verbatim: inline `\\H` does NOT
 * multiply the drop (it sets a RUN's `heightWorld`, never the line step, which
 * `sceneTextMeshFactories.ts` builds from the entity's nominal height); and a run on line 0 gives
 * EXACTLY 0 drop at every `lineSpacingFactor`, so a single-line or first-line-only `\\Q` leans
 * strictly one way. The old expression had neither property.
 *
 * # What the interval still does NOT cover
 *
 * Three terms, each in the module header's budget with its measured magnitude and blocker rather
 * than folded in here: the DECORATION quads `\\L` / `\\O` / `\\K`, whose `max(heightWorld × 0.05,
 * 1)` thickness floor is in WORLD units and so is not homogeneous in `nominalHeight` (U2, #796); a
 * REGISTERED SHX font's descent (U1, #795); and `\\S` stack sub-run offsets, which have no
 * production call site at this commit (U5). Folding a non-homogeneous term in would break the
 * annotative law round 7 pinned.
 *
 * For a SINGLE-LINE single-signed run the two extents sum to `|tan| × runHeight`, the scalar this
 * function has always folded into the width; a MULTI-line run's sum
 * grows with the real baseline drop. What the SPLIT adds is placement: without it a default
 * top-left `\\Q-89;A` put its whole ~57-height enlargement RIGHT of the insertion point while the
 * renderer leaned the caps LEFT (@codex PR #794 round 3 P2).
 *
 * # Why nothing is clamped, and why the result is still finite
 *
 * The renderer applies `Math.tan` to the raw radian value with no range check (`mtextParser.ts`
 * stores `val × π / 180` for ANY finite degrees), so `\\Q90;` really does shear by ~1.6e16 heights
 * on screen and a bound that clamped the angle would under-report precisely those glyphs. Unclamped
 * arithmetic can overflow, though, and a non-finite width poisons every consumer of `entityBounds`
 * — so a non-finite sum is discarded in favour of (in order) the upright estimate if it alone is
 * finite, and the plain nominal-height glyph-count estimate, finite whenever `nominalHeight` is (if
 * it is not, the box's HEIGHT is non-finite too, independently of this). The ladder USED to start a
 * rung above both: the group-41 column verbatim, the last path by which a junk column reached the
 * width and in the WORST case (an overflowing inline scale on a title-block MTEXT would have re-run
 * the exact 210 713 blow-up {@link mtextBoxSize} documents). When the painted run is itself
 * degenerate there is no honest finite answer, so the ladder degrades toward the CONTENT-derived
 * value, not toward unrelated file data. Every fallback rung reports `overhangLeft = overhangRight
 * = 0`: its width no longer stands in a known relation to the interval the side extents come from
 * (the shear term was discarded outright), so the rungs place the box exactly as they always did.
 *
 * # The LEFT BEARING, which is a side without being a shear
 *
 * {@link MAX_GLYPH_INK}'s LEFT half ({@link MAX_GLYPH_LEFT_BEARING}) used to be hung to the RIGHT
 * of the anchor with the rest of it: for an un-obliqued line `overhangLeft` was 0, so a
 * left-attached MTEXT's box began exactly at the insertion X while `j` painted `4/42 = 0.0952` run
 * heights to its LEFT, outside cache-direct metadata bounds, selection and the enclosing INSERT
 * footprint (@codex PR #794 round 10 P2, finding B). The scalar `width` is unchanged — the bearing
 * was always inside it — so `localBoxRect` shrinks the upright column by exactly what it pushes the
 * left side out by, and the box SHIFTS rather than grows, riding the SAME `inkFactor` the right
 * reach does.
 */
function mtextEstimatedWidth(args: {
  readonly layout: MtextPaintedLayout;
  readonly nominalHeight: number;
}): MtextEstimatedWidth {
  const { layout, nominalHeight } = args;
  // (1) — and, per (2), the WHOLE upright layout: nothing wraps, so the
  // group-41 column neither caps this nor appears anywhere below. Pen travel
  // plus ONE glyph's ink overhang, each at its own measured bound — see
  // {@link mtextPaintedBound}, {@link MAX_GLYPH_ADVANCE} and
  // {@link MAX_GLYPH_INK}.
  const upright = mtextPaintedBound(layout.lineAdvanceFactor, layout.inkFactor, nominalHeight);
  // (3) — all four `=== 0` keeps the unobliqued path (the overwhelming
  // majority, and every contents without a backslash at all) byte-identical
  // to the bare `upright`, including when a degenerate height or line
  // advance would make `0 × ∞` NaN.
  const unobliqued =
    layout.shearAboveMin === 0 &&
    layout.shearAboveMax === 0 &&
    layout.shearBelowMin === 0 &&
    layout.shearBelowMax === 0;
  // Both interval ends are already in OCS-local units and already correlated
  // with the run that carries them ({@link mtextPaintedLayout}), so each side
  // is simply the widest displacement in its own direction. The BELOW pair is
  // negated because the interval end is `-belowWorld_r`: a POSITIVE oblique
  // throws its caps right and its DESCENDERS left, and a negative one the
  // other way, which is why both signed sides read both pairs (@codex PR #794
  // round 9 P2).
  const shearRight = unobliqued ? 0 : Math.max(layout.shearAboveMax, -layout.shearBelowMin);
  const shearLeft = unobliqued ? 0 : Math.max(-layout.shearAboveMin, layout.shearBelowMax);
  // The left side bearing is ALREADY inside `upright` (it is the left half of
  // `MAX_GLYPH_INK`), so it is added to the SIDE only — never to `bound`.
  const bearing = layout.inkFactor * MAX_GLYPH_LEFT_BEARING * nominalHeight;
  const bound = upright + shearLeft + shearRight;
  // `bound` finite ⇒ `bearing` finite: both terms of `upright` are
  // non-negative, so a finite sum forces `inkFactor × nominalHeight` finite
  // and the bearing is a smaller multiple of exactly that product.
  if (Number.isFinite(bound)) return { width: bound, overhangLeft: shearLeft + bearing, overhangRight: shearRight };
  if (Number.isFinite(upright)) return { width: upright, overhangLeft: 0, overhangRight: 0 };
  return { width: mtextPaintedBound(layout.lineGlyphs, 1, nominalHeight), overhangLeft: 0, overhangRight: 0 };
}

/**
 * The FINITE upper bound on an MTEXT's painted HEIGHT when the writer carried no group 43 — the
 * whole of {@link mtextBoxSize}'s height estimate branch, and the exact counterpart of {@link
 * mtextEstimatedWidth}.
 *
 * # The interval, and why it is the width axis's interval
 *
 *     height = paintedAbove + paintedBelow
 *
 * — the two ends {@link mtextPaintedLayout} already evaluates PER RUN, in OCS-local units, at the
 * point of emit. Nothing new is measured: the `\\Q` lean is `tan_r` times these same two ends, so
 * the box's height and its lean are by construction statements about ONE interval. A maximum over
 * runs of a SUM cannot be decomposed into maxima of its parts without pairing one run's descent
 * against another run's drop, so a second interval for the vertical axis would re-create the
 * correlation defect of rounds 7 and 8 on a fresh axis (@codex PR #794 round 10).
 *
 * # What it fixes
 *
 * It replaced {@link mtextBlockSpan} used ALONE — the LINE GRID and nothing else, `[lowest
 * baseline, +1.00 × nominalHeight]`. All THREE painted things that grid's own "Deliberately NOT
 * covered" lists are terms of the per-run interval: the DESCENT below the lowest baseline (a single
 * line really paints `capTop + descent` = 0.80952 + 0.28571 = 1.09524 nominal heights on
 * `OpenGost-TypeB`, against the grid's 1.00), inline `\\H` (UNBOUNDED here exactly as it was on the
 * width axis before round 6) and the `\\A` baseline offset.
 *
 * # What did NOT move, and why
 *
 *   * **Group 43 is untouched** and wins outright — this branch is not even evaluated when it is
 *     carried.
 *   * **The upper end stays one whole `runHeight_r`**, not the measured 0.80952 cap: tightening it
 *     would NARROW the box AND split the one interval in two, since the `\\Q` lean's upper end is
 *     this same number. The 0.19 of slack is declared in the header's budget.
 *   * **The grid is a FLOOR, not a replacement** ({@link paintedVerticalInterval}): a line painted
 *     at half the nominal height still occupies its whole row, and a trailing empty paragraph its
 *     own.
 *   * **Homogeneity of degree 1 in `nominalHeight` holds** — round 7's annotative law — with the
 *     one documented exception of an ABSOLUTE `\\H<n>;`, which is why
 *     `wcsBoundsLeaf.ts::mtextLocalCorners` RE-EVALUATES a context's box at that context's render
 *     height.
 *   * **Decoration quads stay OUT** — module header U2.
 *
 * # The fallback rung
 *
 * The interval is built from unclamped inline scales, so it can overflow as the width's can
 * (`\\H1e308x;` twice). A non-finite HEIGHT poisons every consumer of `entityBounds`, so the
 * estimate is discarded for the line grid — content-derived, finite whenever `nominalHeight` and
 * `lineSpacingFactor` are, and precisely what this branch used to return unconditionally. The rung
 * is reported ENTIRELY ABOVE the baseline (`paintedBelow = 0`), so the mesh rectangle {@link
 * mtextAnchoredBoxCorners} unions in stays finite too.
 *
 * The two ENDS and not the scalar, because the MESH anchors this same interval at line 0's baseline
 * while group 71 anchors the height: the union needs the SPLIT, threaded from here rather than
 * re-derived at the corner builder for the reason {@link MtextPaintedExtents} records.
 */
function mtextEstimatedHeightInterval(
  layout: MtextPaintedLayout,
  nominalHeight: number,
  lineSpacingFactor: number,
): { paintedAbove: number; paintedBelow: number } {
  const span = layout.paintedAbove + layout.paintedBelow;
  if (Number.isFinite(span)) return { paintedAbove: layout.paintedAbove, paintedBelow: layout.paintedBelow };
  return { paintedAbove: mtextBlockSpan(nominalHeight, layout.lineCount, lineSpacingFactor), paintedBelow: 0 };
}

/**
 * MTEXT's OCS-local box size, per module doc's authoritative-width priority.
 *
 * `width`/`height` mean exactly what they always meant — the SEMANTIC box's
 * total OCS-local extents, shear included, and an authoritative group 42/43
 * VERBATIM. {@link MtextSideExtents} splits the horizontal ones into their
 * two SIDES so the corner builder can place them where the renderer actually
 * paints: the `\\Q` lean on whichever side it leans, and
 * {@link MAX_GLYPH_LEFT_BEARING} always on the left. `overhangRight` is 0 for
 * the group-42 branch, for an unobliqued run and on every non-finite
 * fallback rung; `overhangLeft` likewise, except that the estimate branch
 * always carries the bearing. {@link MtextPaintedExtents} then adds the
 * MESH's own four reaches, which come from the CONTENTS WALK on EVERY branch
 * — an authoritative group 42/43 describes AutoCAD's box, never the ink this
 * renderer paints (round 12 P2). A `{width, height}`-only consumer is
 * unchanged.
 *
 * A DEGENERATE `nominalHeight` (non-finite, or `<= 0`) is clamped to 0 once,
 * here, before the walk: the contents paint nothing at such a height and a
 * `NaN` reach would blank every consumer of `entityBounds` the way the
 * module header's group-41 incident did. Groups 42/43 are still reported
 * verbatim in that case — they are exact, and the clamp touches only the
 * walk.
 *
 * `nominalHeight` is the height the CONTENTS are laid out against, so an
 * annotative caller that wants a per-scale context's box passes THAT
 * context's render height rather than scaling this function's result — an
 * absolute `\\H<n>;` pins a world height the context scale never reaches
 * (see `wcsBoundsLeaf.ts::mtextLocalCorners`).
 *
 * `referenceWidth` (group 41) stays OPTIONAL on the accepted shape and is deliberately never
 * read: accepting-but-ignoring it keeps every whole-`MtextEntity` caller compiling while saying
 * in the type itself that the wrap column no longer contributes. See the body for why.
 */
export function mtextBoxSize(e: {
  readonly contents: string;
  readonly nominalHeight: number;
  readonly horizontalWidth: number;
  readonly verticalHeight: number;
  readonly referenceWidth?: number;
  readonly lineSpacingFactor: number;
}): { width: number; height: number } & MtextPaintedExtents {
  const contents = e.contents ?? "";
  const lineSpacingFactor = e.lineSpacingFactor > 0 ? e.lineSpacingFactor : 1;
  // The ONE clamp on the layout height — see this function's doc. Everything
  // below is a multiple of it, so a single guard here is what keeps a
  // degenerate entity out of every reach the walk reports.
  const nominalHeight = Number.isFinite(e.nominalHeight) && e.nominalHeight > 0 ? e.nominalHeight : 0;

  // What the contents actually PAINT — paragraph count, the widest line's
  // run-by-run advance, the tallest painted run and the `\Q` lean. The
  // paragraph count comes from HERE and not from a `split(/\P|\n/)` over the
  // raw contents, because that regex cannot tell a paragraph code from the
  // second backslash of an escaped one and does not know `\p` at all
  // (@codex PR #794 round 7 P2 — {@link mtextPaintedLayout}).
  //
  // It runs on EVERY branch, groups 42/43 carried or not — the round-12
  // one-line bands the authoritative branch used instead were far too
  // shallow for a multi-paragraph block, and one walk covers paragraph
  // stacking, inline `\H`, the `\A` offset, the descent and the bearing in
  // ONE derivation (header F1 has the witness and the declared cost, which
  // is that the authoritative branch no longer skips the contents;
  // `mtextPaintedLayout` keeps its own no-backslash fast path).
  const layout = mtextPaintedLayout(contents, nominalHeight, lineSpacingFactor);

  // The painted y-interval the SAME per-run walk gives the `\Q` lean
  // ({@link mtextEstimatedHeightInterval}) — the line grid
  // ({@link mtextBlockSpan}) as a FLOOR, pushed out by the descent, inline
  // `\H` and `\A`, none of which the grid can see (@codex PR #794 round 4 P2
  // for the grid itself, round 10 for the interval) — and the painted
  // horizontal extent ({@link mtextEstimatedWidth}), run by run at each run's
  // own scale.
  const painted = mtextEstimatedHeightInterval(layout, nominalHeight, lineSpacingFactor);
  const estimated = mtextEstimatedWidth({ layout, nominalHeight });

  // The MESH rectangle, about the PEN ORIGIN — line 0's baseline at the
  // layout column's `x = 0`, group 71 and groups 42/43 all unread, because
  // the live renderer reads none of them. Identical on every branch: the pen
  // starts at `x = 0`, `overhangLeft` of painted ink reaches back before it
  // and the rest of the estimated width runs forward.
  //
  // `paintedRight` is the WALKED estimate and not group 42 even where group
  // 42 is carried. The two are statements about different things: group 42
  // is AutoCAD's column for ITS font, the walk is this renderer's reach with
  // the BUNDLED atlases, and the mesh rectangle may only state the second.
  // Neither is dropped — the SEMANTIC rectangle carries group 42 verbatim at
  // its group-71 placement and {@link mtextAnchoredBoxCorners} unions the two.
  //
  // `selectPaintedRight` is the SAME walk clamped by group 42 where group 42
  // is carried, and the walk itself where it is not. BOUNDS keep the walk (a
  // prefilter may over-report, and the walk is what covers a bundled font
  // WIDER than AutoCAD's); the EXACT select ring may not, and 1.22 heights
  // per glyph against a real ≈0.5 put 62 empty units of the reviewer's
  // witness inside the pick box (@codex PR #794 round 13 P2). One `Math.min`
  // over two numbers already computed — no second derivation.
  //
  // The clamp READS group 42 as a description of the CURRENT layout, held up by
  // the WRITE paths: an entity edit and a TEXT STYLE edit CLEAR 42/43 (rounds 14
  // + 15 P2), SCALE carries or clears them (round 16 P2, #804).
  // `mtextCachedWidthCouldDescribeContents` is a last line UNDER those, not a
  // substitute — a COARSE PLAUSIBILITY FLOOR, refusing the clamp only when group
  // 42 is implausibly SMALL for this walk. Header F1's last paragraph has both.
  const walkedRight = estimated.width - estimated.overhangLeft;
  const clampable =
    e.horizontalWidth > 0 &&
    mtextCachedWidthCouldDescribeContents(e.horizontalWidth, layout.lineAdvanceFactor, nominalHeight);
  const mesh = {
    paintedAbove: painted.paintedAbove,
    paintedBelow: painted.paintedBelow,
    paintedLeft: estimated.overhangLeft,
    paintedRight: walkedRight,
    selectPaintedRight: clampable ? Math.min(walkedRight, e.horizontalWidth) : walkedRight,
  };

  // The SEMANTIC box. Groups 42 and 43 are AutoCAD's OWN computed dimensions
  // and stay authoritative, untouched and VERBATIM — the group-42 width gets
  // no side term either (pre-existing behaviour), so that rectangle's
  // placement stays symmetric-by-attachment. "Authoritative" is a statement
  // about a measurement of the CURRENT contents: an EDITED MTEXT used to keep
  // the imported pair and state a rectangle for a string it no longer held,
  // which the write path's round-14 invalidation is what fixes (see the
  // `selectPaintedRight` note above — the same clearing serves both facets).
  //
  // `referenceWidth` (group 41) is the WRAP COLUMN and plays NO part in the
  // width — {@link mtextEstimatedWidth}'s term (2) carries the end-to-end
  // evidence that nothing in this repo wraps, and the incident: taken
  // VERBATIM, a real AutoCAD 2018 title-block MTEXT reading "1П30.18-30" (10
  // chars at height 350, no group 42/43, group 41 = 210 713.35) blew its
  // ×100-context BLOCK's bounds out to ±10 535 667 in X against real content
  // of 3000 × 1750; 83 INSERTs of it stretched the model fit box from ~2.3 M
  // to 21.6 M units, the fit zoom collapsed to 3.3e-5 and
  // `partitionStubsByLod` dropped 7 103 of 8 422 entities as sub-pixel — a
  // BLANK drawing. `Math.min(referenceWidth, estimate)` traded that for an
  // UNDER-report on the other side of the `min` (@codex PR #794 round 5 P2).
  // KNOWN, not addressed here: the height counts PARAGRAPHS, not wrapped
  // lines — U4.
  const height = e.verticalHeight > 0 ? e.verticalHeight : painted.paintedAbove + painted.paintedBelow;
  if (e.horizontalWidth > 0) {
    return { width: e.horizontalWidth, height, ...NO_OVERHANG, ...mesh };
  }
  return {
    width: estimated.width,
    height,
    overhangLeft: estimated.overhangLeft,
    overhangRight: estimated.overhangRight,
    ...mesh,
  };
}

/**
 * MTEXT's EFFECTIVE angle (degrees): the group-11 X-axis DIRECTION VECTOR takes precedence over
 * the `rotation` scalar (group 50) WHEN it is custom (Autodesk DXF reference + ezdxf
 * `MText.get_rotation()`) — an imported MTEXT with `rotation=0` but a genuinely custom group-11
 * direction is drawn (and must therefore be BOUNDED) at the vector's angle, not 0°.
 */
export function mtextEffectiveAngleDeg(xAxisDirection: { x: number; y: number; z?: number }, rotationDeg: number): number {
  const isCustom = !(xAxisDirection.x === 1 && xAxisDirection.y === 0 && (xAxisDirection.z ?? 0) === 0);
  return isCustom ? (Math.atan2(xAxisDirection.y, xAxisDirection.x) * 180) / Math.PI : rotationDeg;
}

/**
 * MTEXT's 1-9 `attachmentPoint` grid anchor corners (group 71 — Autodesk DXF reference: 1=TL,
 * 2=TC, 3=TR, 4=ML, 5=MC, 6=MR, 7=BL, 8=BC, 9=BR), rotated by `rotationDeg` about `insertion`.
 *
 * # It returns the HULL of two rectangles, because two things are placed
 *
 * The DXF semantics place the box by group 71; the live MESH paints every MTEXT as if it were
 * BOTTOM-LEFT attached on line 0's baseline, whatever group 71 says. Module header F1 has the
 * derivation, the measured escape and the declared cost (@codex PR #794 round 10 P2, finding
 * C; #801, #802). {@link mtextLocalBoxRects} builds BOTH in the SAME local frame; this unions
 * them and anchors + rotates the union ONCE — unioning in world coordinates, or rotating each
 * rectangle separately, would both be wrong for a rotated entity. BOUNDS want exactly this
 * hull; an EXACT select test wants {@link mtextAnchoredBoxRings} instead (round 11, finding 2).
 *
 * `extents` ({@link MtextPaintedExtents}, from {@link mtextBoxSize}) carries both halves of
 * what the union needs: the two SIDE extents of the SEMANTIC rectangle — the `\\Q` oblique
 * enlargement already contained in `width` plus the left side bearing — and the MESH
 * rectangle's own four reaches. The ATTACHMENT anchors the UPRIGHT layout column, `width −
 * overhangLeft − overhangRight`, and each side is then hung off where the ink really is, so the
 * box covers the vertices the renderer paints instead of an equally wide box on the wrong side
 * of the anchor (@codex PR #794 round 3 P2). OMITTING it means no overhang and a mesh rectangle
 * of `x ∈ [0, width]`, `y ∈ [0, height]` — the group-42/43 convention.
 */
export function mtextAnchoredBoxCorners(
  insertion: Local2,
  width: number,
  height: number,
  attachmentPoint: number | undefined,
  rotationDeg: number,
  extents?: MtextPaintedExtents,
  mirrored = false,
): Local2[] {
  const [semantic, mesh] = mtextLocalBoxRects(width, height, attachmentPoint, extents, mirrored, "bounds");
  return anchorAndRotate(insertion, rectCorners(unionRect(semantic, mesh)), rotationDeg);
}

/**
 * The SAME two rectangles {@link mtextAnchoredBoxCorners} bounds, as TWO SEPARATE CLOSED RINGS
 * (4 corners each, anchored + rotated by the same single transform) — what an EXACT selection
 * test needs, and what a bounding box may not be confused with.
 *
 * # Why select may not take the hull (@codex PR #794 round 11 P2, finding 2)
 *
 * Two rectangles need not cover their own bounding rectangle. At a TOP-RIGHT attachment the
 * group-71 rectangle sits left of and below the insertion point while the mesh rectangle sits
 * right of and above it, so their hull additionally holds a large top-left and a large
 * bottom-right region belonging to NEITHER. Bounds may report that hull — a bounding box IS a
 * hull, and over-reporting a prefilter costs one wasted exact test. `getSelectGeometry` is the
 * exact test: closing the hull's 4 points into one ring (`cad-core/common.ts
 * ::segmentsFromPoints`) would make a crossing window drawn through one of those EMPTY regions
 * select the MTEXT. So the two rings stay two: 8 points for the "every point inside" window
 * test (the entity is enclosed only when BOTH rectangles are) and 8 segments for the crossing
 * test, one quad's worth per rectangle. #801 collapses the two into one.
 *
 * # …and why its MESH ring is NARROWER than the one BOUNDS get (round 13 P2)
 *
 * The same principle, one axis over. The mesh rectangle's right edge here is
 * `selectPaintedRight` ({@link MtextPaintedExtents}) — the contents walk clamped by group 42
 * where the file carries one — while {@link mtextAnchoredBoxCorners} unions the unclamped
 * `paintedRight`. The walk bounds every glyph by {@link MAX_GLYPH_ADVANCE}, 1.22 heights
 * against a bundled-prose average nearer 0.5, so on a 36-glyph authoritative-42 witness it
 * reaches 112.785 where the atlases paint 26-31 and group 42 ends at 50: as a PREFILTER that
 * is a wasted exact test, as an EXACT ring it is 62 units of empty strip that select the
 * MTEXT. Group 42 is AutoCAD's measurement of the SAME string and still covers the ink. The
 * two facets therefore diverge a second time, deliberately and in ONE direction: BOUNDS ⊇
 * SELECT, always. DECLARED COST: where the bundled atlases really are wider than AutoCAD's
 * font the exact ring can now stop short of painted ink on X — the prefilter still covers it,
 * and U1/#795 (real resolved-style metrics) deletes the gap between the two numbers outright.
 * Y is NOT clamped the same way: group 43 does not cover the descent below line 0's baseline
 * (round 11, finding 1) and the Y walk over-reports by ≈1.2×, not 3.4× — header F1.
 */
export function mtextAnchoredBoxRings(
  insertion: Local2,
  width: number,
  height: number,
  attachmentPoint: number | undefined,
  rotationDeg: number,
  extents?: MtextPaintedExtents,
  mirrored = false,
): Local2[][] {
  return mtextLocalBoxRects(width, height, attachmentPoint, extents, mirrored, "select").map((rect) =>
    anchorAndRotate(insertion, rectCorners(rect), rotationDeg),
  );
}

/**
 * Which consumer {@link mtextLocalBoxRects} is building for. The two differ in exactly one
 * number — the mesh rectangle's right edge — and the builder stays the one place either
 * rectangle is built, so the divergence is a parameter of the shared truth rather than a
 * second box (round 13 P2; {@link mtextAnchoredBoxRings} has the derivation).
 */
type MtextRectFacet = "bounds" | "select";

/**
 * The two LOCAL (anchor-relative, PRE-rotation) rectangles an MTEXT occupies — `[semantic,
 * mesh]`, the ONE place either is built, so the bounds hull and the select rings cannot drift
 * into describing different boxes. Where they DO differ they differ by `facet` alone, in
 * exactly one number the caller does not get to pick (see {@link MtextRectFacet}).
 *
 * `mirrored` is the group-210 handedness bit (`wcsBoundsMath.ts::isReflectedOcs`): the renderer
 * draws a reflected MTEXT with `mesh.scale.set(1, -1, 1)`
 * (`sceneTextMeshFactories.ts::applyMirrorScale`) in the mesh's OWN pre-rotation frame —
 * exactly this frame — so the painted y-interval is NEGATED end for end, `[-paintedAbove,
 * +paintedBelow]`. Unioning the unmirrored interval instead put a bottom-attached reflected
 * run's glyphs OUTSIDE both the metadata bounds and the pick box, unboundedly so with an inline
 * `\H` (round 11 P2, finding 3). X is untouched: the scale flips Y only.
 */
function mtextLocalBoxRects(
  width: number,
  height: number,
  attachmentPoint: number | undefined,
  extents: MtextPaintedExtents | undefined,
  mirrored: boolean,
  facet: MtextRectFacet,
): [LocalRect, LocalRect] {
  const a = attachmentPoint !== undefined && attachmentPoint >= 1 && attachmentPoint <= 9 ? attachmentPoint : 1;
  // Column: 1/4/7 = Left, 2/5/8 = Center, 3/6/9 = Right.
  const col = (a - 1) % 3;
  // DXF row-of-3: 0 = Top (1-3), 1 = Middle (4-6), 2 = Bottom (7-9) — INVERTED here (2 - dxfRow)
  // to match `localBoxRect`'s own row convention (0=bottom-anchored/extends+Y, 2=top-anchored/
  // extends−Y — see its doc). @codex T1 arm(c) round-5 P2 finding: without the inversion, a
  // default `attachmentPoint=1` (Top-Left) MTEXT was anchored as BOTTOM (row=0 uninverted),
  // extending the box UPWARD from the insertion point instead of the correct DOWNWARD — reporting
  // `entityBounds` vertically flipped (missing the real text, including empty space above it).
  const dxfRow = Math.floor((a - 1) / 3);
  const semantic = localBoxRect(width, height, col, 2 - dxfRow, extents);
  // What the mesh paints, about the pen origin — line 0's baseline at the layout column's
  // `x = 0`, group 71 unread. The four reaches are threaded from the same walk the width and
  // height came from ({@link MtextPaintedExtents}), never re-derived here; OMITTING them is the
  // group-42/43 convention, `x ∈ [0, width]` and `y ∈ [0, height]` — except for the RIGHT edge,
  // the one number the two facets disagree on: BOUNDS take the unclamped walk, SELECT the same
  // walk clamped by group 42 where the file carries one, since a prefilter may over-report and
  // an exact crossing ring may not (round 13 P2 — the derivation, the witness and the declared
  // cost are on {@link mtextAnchoredBoxRings}).
  const above = extents?.paintedAbove ?? height;
  const below = extents?.paintedBelow ?? 0;
  const mesh: LocalRect = {
    left: -(extents?.paintedLeft ?? 0),
    right: extents ? (facet === "select" ? extents.selectPaintedRight : extents.paintedRight) : width,
    bottom: mirrored ? -above : -below,
    top: mirrored ? below : above,
  };
  return [semantic, mesh];
}

/**
 * {@link localBoxRect}'s corners, with the DESCENDER BAND the single-line TEXT / ATTDEF box
 * needs below its baseline.
 *
 * `textJustifiedLocalBox` maps the baseline to the box's BOTTOM edge for EVERY row (the row
 * only decides where that edge is), and the bundled renderer paints descenders
 * {@link MAX_GLYPH_DESCENT} × `height` BELOW the baseline (`textRunRenderer.ts` puts a quad's
 * lower edge at `cursorY + (bearingY − h) × heightScale`; `OpenGost`'s `@` reaches 12/42 =
 * 0.2857 heights down). That ink used to sit OUTSIDE cache-direct metadata bounds, selection
 * bounds and every enclosing INSERT footprint (@codex PR #794 round 10 P2, finding A).
 *
 * The band is added HERE and not in {@link localBoxRect} because MTEXT must not get it twice:
 * its descent is already inside `paintedBelow` ({@link paintedVerticalInterval}).
 */
function textLocalBoxCorners(width: number, height: number, col: number, row: number): Local2[] {
  const box = localBoxRect(width, height, col, row);
  return rectCorners({ ...box, bottom: box.bottom - MAX_GLYPH_DESCENT * height });
}
